import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Alert // Import Alert for better error display
} from '@mui/material';
import { styled } from '@mui/system';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import MovieIcon from '@mui/icons-material/Movie';
import TuneIcon from '@mui/icons-material/Tune';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import PublishIcon from '@mui/icons-material/Publish';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import GridOnIcon from '@mui/icons-material/GridOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoopIcon from '@mui/icons-material/Loop';

import { runComfyDeployWorkflow } from '../api/comfyDeployApi';
import { canvasWorkflows } from '../workflows/canvasWorkflows';
import { supabase, consumeCredits } from '../supabaseClient';

// Find the video generator workflow
const videoWorkflow = canvasWorkflows.find(w => w.id === 'video-generator');

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadArea = styled(Box)(({ theme, isDragging, hasFile }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: 16,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: isDragging
    ? `${theme.palette.primary.main}10`
    : hasFile
      ? `${theme.palette.success.main}10`
      : theme.palette.mode === 'dark'
        ? 'rgba(18, 18, 23, 0.6)'
        : 'rgba(255, 255, 255, 0.6)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  minHeight: 200,
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}10`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: theme => `
      radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 70%),
      repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px),
      repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px)
    `,
    backgroundSize: '100% 100%, 20px 20px, 20px 20px',
    opacity: 0.5,
    zIndex: 0,
  }
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.05)',
  position: 'relative',
  aspectRatio: '16/9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

const ControlButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
  '&:hover': {
    boxShadow: `0 6px 16px ${theme.palette.primary.main}40`,
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '12px 12px 0 0',
  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(32, 178, 170, 0.1)'
      : 'rgba(0, 128, 128, 0.1)',
  }
}));

const VideoGenerator = ({ session }) => {
  const theme = useTheme();
  const [mode, setMode] = useState('quick'); // 'quick' or 'studio'
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null); // State for error messages

  // Camera movement settings
  const [cameraMovement, setCameraMovement] = useState('pan_right');
  const [duration, setDuration] = useState('4');
  const [quality, setQuality] = useState('standard');

  // Advanced settings (for studio mode)
  const [keyframes, setKeyframes] = useState([]);
  const [selectedKeyframe, setSelectedKeyframe] = useState(null);
  const [studioLayout, setStudioLayout] = useState('four-panel'); // 'four-panel' or 'preview-focus'
  const [timelineZoom, setTimelineZoom] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(4); // in seconds
  const [cameraPath, setCameraPath] = useState('linear'); // 'linear', 'bezier', 'custom'
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetLibrary, setAssetLibrary] = useState([]);
  const [videoOutputs, setVideoOutputs] = useState([]); // Array of video outputs on the timeline
  const [selectedOutput, setSelectedOutput] = useState(null); // Currently selected output

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle mode change
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
      // Optionally reset settings or state when switching modes
    }
  };

  // Effect to revoke object URLs when they are no longer needed
  useEffect(() => {
    // Store the current URL in a variable accessible by the cleanup function
    const currentPreviewUrl = previewUrl;

    // Return a cleanup function that runs when previewUrl changes or component unmounts
    return () => {
      if (currentPreviewUrl && currentPreviewUrl.startsWith('blob:')) {
        // Check if this URL is still used in the asset library *at the time of cleanup*
        // We need to access the latest assetLibrary state here.
        // A simple way is to ensure assetLibrary is in the dependency array.
        const isUsedInLibrary = assetLibrary.some(asset => asset.url === currentPreviewUrl);

        if (!isUsedInLibrary) {
          // Only revoke if it's a blob URL and not found in the current asset library
          try {
            URL.revokeObjectURL(currentPreviewUrl);
            // console.log('Revoked Object URL (previewUrl):', currentPreviewUrl);
          } catch (error) {
            console.error('Error revoking previewUrl:', currentPreviewUrl, error);
          }
        } else {
          // console.log('Skipping revoke for previewUrl as it is used in assetLibrary:', currentPreviewUrl);
        }
      }
    };
  }, [previewUrl, assetLibrary]); // Add assetLibrary dependency

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        // Add to library (this handles duplicates and URL creation/updates)
        const asset = addAssetToLibrary(file);

        if (asset) {
          // Set as main image using the URL from the asset (might be new or existing)
          setUploadedImage(file);
          setPreviewUrl(asset.url); // Use the URL from the potentially updated asset
          setGeneratedVideo(null);
          setError(null);
          selectAsset(asset.id); // Ensure the correct asset is selected
          console.log('Image uploaded/selected successfully:', file.name);
        } else {
          // addAssetToLibrary returned null, likely an error occurred
          setError('Görsel kütüphaneye eklenemedi.');
        }
      } catch (error) {
        console.error('Error handling file upload:', error);
        setError('Görsel yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } else if (file) {
      setError('Lütfen geçerli bir görsel dosyası yükleyin (.jpg, .png, .webp)');
    }
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if the leave target is outside the current target
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Explicitly show this is a copy operation
    e.dataTransfer.dropEffect = 'copy';
    // Keep the dragging state active
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    try {
      // Handle multiple files
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      
      // Process all dropped files that are images
      let firstImageFile = null;
      let firstImageAsset = null; // Store the asset corresponding to the first image
      let addedCount = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file && file.type.startsWith('image/')) {
          // Add to library (handles duplicates and URL creation/updates)
          const asset = addAssetToLibrary(file);
          if (asset) {
            // Check if it was newly added or just updated/selected
            // We can't easily tell from the return value alone, but addAssetToLibrary logs it.
            // Let's assume addAssetToLibrary handles the logic correctly.

            // Store the first valid image file and its corresponding asset
            if (!firstImageFile) {
              firstImageFile = file;
              firstImageAsset = asset;
            }
          }
        }
      }

      // Set the first image as the main image if we found one
      if (firstImageFile && firstImageAsset) {
        setUploadedImage(firstImageFile);
        setPreviewUrl(firstImageAsset.url); // Use the URL from the asset
        setGeneratedVideo(null);
        setError(null);
        selectAsset(firstImageAsset.id); // Select the first added/found asset
      }

      console.log(`Processed ${files.length} dropped files`); // Simplified log
    } catch (error) {
      console.error('Error handling dropped files:', error);
      setError('Görsel(ler) işlenirken bir hata oluştu.');
    }
  };

  // Generate video
  const handleGenerateVideo = async () => {
    if (!uploadedImage || !session || !videoWorkflow) {
        console.error('Missing requirements for video generation:', { hasImage: !!uploadedImage, hasSession: !!session, hasWorkflow: !!videoWorkflow });
        setError('Gerekli bilgiler eksik. Lütfen bir resim yükleyin ve giriş yaptığınızdan emin olun.'); // User-friendly error
        return;
    }

    // Reset all states to ensure clean start
    setIsGenerating(true);
    setProgress(0);
    setGeneratedVideo(null); // Clear previous video before starting new generation
    setError(null); // Clear previous errors
    
    // Force UI update before proceeding
    await new Promise(resolve => setTimeout(resolve, 100));

    // Check and consume credits before generation
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error('Failed to authenticate user');
      
      const requiredCredits = 30; // Fixed 30 credits per video
      const { error: creditError } = await consumeCredits(user.id, requiredCredits);
      
      if (creditError) {
        throw new Error(creditError.message || 'Kredi yetersiz veya işlem başarısız oldu. Lütfen kontrol edin.');
      }
    } catch (error) {
      console.error('Credit consumption error:', error);
      setError(error.message);
      setIsGenerating(false); // Ensure this is set
      setProgress(0); // Ensure progress is reset
       
      // Redirect to subscription page if credits are insufficient
      if (error.message.includes('Insufficient credits')) {
        window.location.href = '/subscription';
      }
      return;
    }
    
    // For studio mode, automatically create a new output in the timeline
    if (mode === 'studio') {
      console.log('Studio mode video generation initiated');
      
      // Check if we're already generating a video
      const isGeneratingAny = videoOutputs.some(o => o.status === 'generating');
      if (isGeneratingAny) {
        setError('Lütfen mevcut video oluşturma işleminin tamamlanmasını bekleyin.');
        setIsGenerating(false);
        return;
      }
      
      // Find the selected output or the first pending output if any exists
      const selectedPendingOutput = videoOutputs.find(o => o.id === selectedOutput && o.status === 'pending');
      const firstPendingOutput = videoOutputs.find(o => o.status === 'pending');
      const outputToGenerate = selectedPendingOutput || firstPendingOutput;
      
      if (outputToGenerate) {
        console.log('Found pending output to generate:', outputToGenerate.id);
        // Generate this output
        try {
          await handleGenerateVideoOutput(outputToGenerate.id);
          return; // Don't set isGenerating to false here as handleGenerateVideoOutput will handle it
        } catch (error) {
          console.error('Error generating video from timeline:', error);
          setError('Video oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
          setIsGenerating(false);
          return;
        }
      }
      
      console.log('Creating new output for video generation');
      // Always create a new output at current time when generating in studio mode
      // Create a unique ID with timestamp and random component
      const uniqueId = Date.now() + Math.floor(Math.random() * 1000);
      
      // Find a suitable position for the new video
      const existingPositions = [...videoOutputs]
        .sort((a, b) => a.startTime - b.startTime)
        .map(o => ({
          start: o.startTime,
          end: o.endTime
        }));
      
      let newStartTime = currentTime;
      let newEndTime = Math.min(totalDuration, currentTime + 1);
      
      // Check for overlaps
      const hasOverlap = existingPositions.some(pos => 
        (newStartTime < pos.end && newEndTime > pos.start)
      );
      
      if (hasOverlap) {
        // Find the first available gap
        for (let t = 0; t < totalDuration - 1; t += 0.5) {
          const testStart = t;
          const testEnd = Math.min(totalDuration, t + 1);
          const overlaps = existingPositions.some(pos => 
            (testStart < pos.end && testEnd > pos.start)
          );
          
          if (!overlaps) {
            newStartTime = testStart;
            newEndTime = testEnd;
            break;
          }
        }
      }
      
      const newOutput = {
        id: uniqueId,
        startTime: newStartTime,
        endTime: newEndTime,
        type: 'video',
        settings: {
          cameraMovement: cameraMovement,
          quality: quality,
          assetId: selectedAsset || 'main'
        },
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      console.log('Adding new output to timeline:', newOutput);
      
      // Use a functional update and store the new outputs
      let newOutputs;
      await new Promise(resolve => {
        setVideoOutputs(prevOutputs => {
          newOutputs = [...prevOutputs, newOutput];
          resolve();
          return newOutputs;
        });
      });
      
      // Verify the output was added
      const outputExists = newOutputs.some(o => o.id === uniqueId);
      if (!outputExists) {
        console.error('Failed to add output to timeline:', uniqueId, 'Current outputs:', newOutputs);
        setError('Video çıktısı zaman çizelgesine eklenemedi. Lütfen tekrar deneyin.');
        setIsGenerating(false);
        return;
      }
      
      // Now set the selected output
      setSelectedOutput(uniqueId);
      
      // Small delay to ensure React has processed all state updates
      await new Promise(resolve => setTimeout(resolve, 50));
      
      console.log('Starting generation for new output:', uniqueId);
      try {
        await handleGenerateVideoOutput(uniqueId);
      } catch (error) {
        console.error('Error generating new video output:', error);
        setError('Video oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
        setIsGenerating(false);
      }
      
      return;
    }
    
    // Track the workflow run ID for debugging
    let currentRunId = null;

    try {
      // Set a timeout for the entire generation process
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        // Create a timeout that will reject the promise after 5 minutes
        timeoutId = setTimeout(() => {
          reject(new Error('Video oluşturma 5 dakika içinde zaman aşımına uğradı. Lütfen tekrar deneyin.'));
        }, 5 * 60 * 1000); // 5 minutes timeout
      });


      // 1. Upload to Supabase storage
      const fileExt = uploadedImage.name.split('.').pop()?.toLowerCase(); // Ensure lowercase extension
      const fileName = `video-input-${session.user.id}-${Date.now()}.${fileExt}`; // Add user ID for better tracking/policy
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('uploaded-images') // Ensure this bucket exists and has correct policies
        .upload(fileName, uploadedImage, {
            cacheControl: '3600', // Optional: cache control
            upsert: false // Prevent overwriting unless intended
        });

      if (uploadError) {
        console.error('Supabase upload error:', uploadError);
        throw new Error(`Resim yüklenemedi: ${uploadError.message}`); // Turkish error
      }
      if (!uploadData?.path) {
          throw new Error('Supabase upload succeeded but did not return a path.');
      }


      // 2. Get the public URL
      const { data: urlData } = supabase.storage
        .from('uploaded-images')
        .getPublicUrl(uploadData.path);

      if (!urlData?.publicUrl) {
          throw new Error('Yüklenen resmin genel URL\'si alınamadı.'); // Turkish error
      }
      const publicUrl = urlData.publicUrl;

      // 3. Prepare inputs for ComfyDeploy workflow
      let selectedImageUrl = publicUrl;
      
      // If in studio mode and a specific asset is selected, use that instead
      if (mode === 'studio' && selectedAsset) {
        if (selectedAsset === 'main') {
          // Use the main uploaded image
          selectedImageUrl = publicUrl;
        } else {
          // Find the selected asset in the asset library
          const selectedAssetObj = assetLibrary.find(asset => asset.id === selectedAsset);
          if (selectedAssetObj && selectedAssetObj.url) {
            // We need to upload this asset to Supabase first to get a public URL
            try {
              const assetFile = selectedAssetObj.file;
              const fileExt = assetFile.name.split('.').pop()?.toLowerCase();
              const fileName = `video-input-${session.user.id}-${Date.now()}-${selectedAsset}.${fileExt}`;
              
              const { data: assetUploadData, error: assetUploadError } = await supabase.storage
                .from('uploaded-images')
                .upload(fileName, assetFile, {
                  cacheControl: '3600',
                  upsert: false
                });
                
              if (assetUploadError) {
                console.error('Error uploading selected asset:', assetUploadError);
                // Fall back to the main image
              } else {
                // Get the public URL for the uploaded asset
                const { data: assetUrlData } = supabase.storage
                  .from('uploaded-images')
                  .getPublicUrl(assetUploadData.path);
                  
                if (assetUrlData?.publicUrl) {
                  console.log('Using selected asset from library:', assetUrlData.publicUrl);
                  selectedImageUrl = assetUrlData.publicUrl;
                }
              }
            } catch (assetError) {
              console.error('Error processing selected asset:', assetError);
              // Fall back to the main image
            }
          }
        }
      }
      
      const inputs = {
        // Use keys expected by the specific ComfyDeploy workflow
        input_image: selectedImageUrl, // Use the selected image URL
        camera_movement: cameraMovement,
        duration: parseInt(duration, 10), // Send as number if API expects number
        quality: quality,
        // Add other inputs required by the workflow
      };

      // Add studio mode specific parameters if in studio mode
      if (mode === 'studio') {
        // Add keyframe data if keyframes exist
        if (keyframes.length > 0) {
          inputs.keyframes = JSON.stringify(keyframes);
        }
        
        // Add camera path type
        inputs.camera_path = cameraPath;
        
        // Add any other studio-specific parameters
        inputs.studio_mode = true;
      }

      // 4. Run the workflow
      console.log('Running workflow with ID:', videoWorkflow.deploymentId, 'and inputs:', inputs);

      let videoUrlFound = false; // Flag to track if URL found in callback
      let finalWorkflowOutput = null; // Store final output
      let workflowRunId = null; // Store the run ID for debugging

      // Define the workflow promise
      const workflowPromise = runComfyDeployWorkflow(
        videoWorkflow.deploymentId,
        inputs,
        (progressValue, currentOutput) => { // Progress/Intermediate output callback
          const newProgress = Math.min(progressValue * 100, 100);
          console.log('Setting progress:', newProgress);
          
          // Force a state update with setTimeout to ensure React renders the change
          setProgress(newProgress);
          console.log('Progress updated to UI:', newProgress);
          
          // Force DOM update by using document.title as a side effect
          document.title = `Progress: ${Math.round(newProgress)}% - MimarAI`;

          // Process intermediate output - structure depends heavily on ComfyDeploy setup
          console.log('Workflow Progress:', progressValue, 'Output:', currentOutput);
          
          // Store the run ID if available
          if (currentOutput?.run_id && !workflowRunId) {
            workflowRunId = currentOutput.run_id;
            currentRunId = workflowRunId;
            console.log('Workflow Run ID:', workflowRunId);
          }
          
          // Check if this is a final output (progress = 1.0)
          if (progressValue >= 0.99) {
            console.log('Final output received:', currentOutput);
                
            // If we have a URL in the output, use it directly
            // We'll handle the Supabase upload after the workflow completes
            if (currentOutput?.url) {
              console.log('URL found in final output:', currentOutput.url);
              setGeneratedVideo(currentOutput.url);
              videoUrlFound = true;
              return; // Exit the callback early
            }
          }
              
          let intermediateUrl = null;

          // Example checks (adjust based on actual workflow output structure)
          if (currentOutput?.url && (currentOutput.url.endsWith('.webp') || currentOutput.url.endsWith('.mp4'))) {
             intermediateUrl = currentOutput.url;
          } else if (currentOutput?.data?.videos?.[0]?.url) { // Check nested structures
             intermediateUrl = currentOutput.data.videos[0].url;
          } else if (currentOutput?.data?.images?.[0]?.url) {
             // For images, check if the URL ends with .webp (which can be animated)
             const isWebp = currentOutput.data.images[0].url.endsWith('.webp');
             if (isWebp) {
               intermediateUrl = currentOutput.data.images[0].url;
             }
          }

          if (intermediateUrl && !videoUrlFound) {
            console.log('Intermediate video/WebP URL found:', intermediateUrl);
            
            // Don't set the ComfyDeploy URL directly - upload to Supabase first
            // We'll temporarily set it to show something to the user
            setGeneratedVideo(intermediateUrl);
            videoUrlFound = true;
            
            // If this is a complete output (progress near 1.0), we can finish early
            if (progressValue >= 0.95) {
              console.log('Video URL found and progress is near completion, finishing early');
              setProgress(100);
              
              // Upload to Supabase immediately to ensure we have a Supabase URL
              (async () => {
                try {
                  const response = await fetch(intermediateUrl);
                  if (!response.ok) {
                    throw new Error(`Failed to fetch video from ComfyDeploy: ${response.status}`);
                  }
                  
                  const blob = await response.blob();
                  if (blob.size === 0) {
                    throw new Error('Fetched blob has zero size');
                  }
                  
                  const fileExtension = intermediateUrl.split('.').pop()?.toLowerCase() || 'webp';
                  const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
                  const contentType = fileExtension === 'webp' ? 'image/webp' : 'video/mp4';
                  
                  const videoFile = new File([blob], fileName, { type: contentType });
                  
                  const { data: uploadData, error: uploadError } = await supabase.storage
                    .from('generated-videos')
                    .upload(`${session.user.id}/${fileName}`, videoFile, {
                      cacheControl: '3600',
                      upsert: true,
                      contentType: contentType
                    });
                    
                  if (uploadError) {
                    console.error('Error uploading to Supabase:', uploadError);
                  } else {
                    const { data: urlData } = supabase.storage
                      .from('generated-videos')
                      .getPublicUrl(uploadData.path);
                      
                    if (urlData?.publicUrl) {
                      console.log('Video uploaded to Supabase, using URL:', urlData.publicUrl);
                      setGeneratedVideo(urlData.publicUrl);
                      await saveVideoMetadata(urlData.publicUrl);
                    }
                  }
                } catch (error) {
                  console.error('Error uploading to Supabase during progress callback:', error);
                } finally {
                  // Always reset generation state, even if there was an error
                  console.log('Resetting generation state after completion');
                  setIsGenerating(false);
                  setProgress(0);
                }
              })();
            }
          }
          // Store the last received output in case the final promise resolves without an explicit return value
          finalWorkflowOutput = currentOutput;
        }
      );

      // Race between the workflow execution and the timeout
      const outputs = await Promise.race([timeoutPromise, workflowPromise]);

      // Cleanup the timeout if workflowPromise finished first
      clearTimeout(timeoutId);

      console.log('Workflow finished. Resolved Output:', outputs);
      
      // Log the full run ID for debugging
      if (workflowRunId) {
        console.log('Completed workflow run ID:', workflowRunId);
      }
      
      // Reset generation states immediately after successful completion
      setIsGenerating(false);
      setProgress(0);

      // 5. Process final outputs if no video found in callback
      if (!videoUrlFound) {
          let finalUrl = null;
          // The 'outputs' might be the direct result or the last 'currentOutput' from callback
          const outputToProcess = outputs || finalWorkflowOutput;

          console.log('Processing final outputs, no video URL found yet. Output to process:', outputToProcess);

          if (outputToProcess) {
              // Check various possible structures in the final output
              if (outputToProcess.url && (outputToProcess.url.endsWith('.webp') || outputToProcess.url.endsWith('.mp4'))) {
                  finalUrl = outputToProcess.url;
                  console.log('Found URL directly in output:', finalUrl);
              } else if (Array.isArray(outputToProcess) && outputToProcess.length > 0) {
                  // If it's an array, find the video output
                  console.log('Output is an array, searching for video URL...');
                  for (const output of outputToProcess) {
                      console.log('Checking output item:', output);
                      if (output.url && (output.url.endsWith('.webp') || output.url.endsWith('.mp4'))) {
                          finalUrl = output.url;
                          console.log('Found URL in array item:', finalUrl);
                          break;
                      } else if (output.data?.videos?.[0]?.url) {
                          finalUrl = output.data.videos[0].url;
                          console.log('Found video URL in data.videos:', finalUrl);
                          break;
                      } else if (output.data?.images?.[0]?.url) {
                          // Check if the URL ends with .webp (which can be animated)
                          const isWebp = output.data.images[0].url.endsWith('.webp');
                          if (isWebp) {
                              finalUrl = output.data.images[0].url;
                              console.log('Found animated image URL:', finalUrl);
                              break;
                          }
                      }
                  }
              } else if (outputToProcess.data?.videos?.[0]?.url) {
                  finalUrl = outputToProcess.data.videos[0].url;
                  console.log('Found video URL in data.videos:', finalUrl);
              } else if (outputToProcess.data?.images?.[0]?.url) {
                  // Check if the URL ends with .webp (which can be animated)
                  const isWebp = outputToProcess.data.images[0].url.endsWith('.webp');
                  if (isWebp) {
                      finalUrl = outputToProcess.data.images[0].url;
                      console.log('Found animated image URL in data.images:', finalUrl);
                  }
              }
          }

          if (finalUrl) {
             console.log('Final video URL found:', finalUrl);
             
             // Don't set the ComfyDeploy URL directly - we'll upload to Supabase
             // But temporarily set it to show something to the user
             setGeneratedVideo(finalUrl);
             videoUrlFound = true;
             
             // Immediately try to upload to Supabase
             (async () => {
               try {
                 const response = await fetch(finalUrl);
                 if (!response.ok) {
                   throw new Error(`Failed to fetch video from ComfyDeploy: ${response.status}`);
                 }
                 
                 const blob = await response.blob();
                 if (blob.size === 0) {
                   throw new Error('Fetched blob has zero size');
                 }
                 
                 const fileExtension = finalUrl.split('.').pop()?.toLowerCase() || 'webp';
                 const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
                 const contentType = fileExtension === 'webp' ? 'image/webp' : 'video/mp4';
                 
                 const videoFile = new File([blob], fileName, { type: contentType });
                 
                 const { data: uploadData, error: uploadError } = await supabase.storage
                   .from('generated-videos')
                   .upload(`${session.user.id}/${fileName}`, videoFile, {
                     cacheControl: '3600',
                     upsert: true,
                     contentType: contentType
                   });
                   
                 if (uploadError) {
                   console.error('Error uploading to Supabase:', uploadError);
                 } else {
                   const { data: urlData } = supabase.storage
                     .from('generated-videos')
                     .getPublicUrl(uploadData.path);
                     
                   if (urlData?.publicUrl) {
                     console.log('Video uploaded to Supabase, using URL:', urlData.publicUrl);
                     setGeneratedVideo(urlData.publicUrl);
                     await saveVideoMetadata(urlData.publicUrl);
                   }
                 }
               } catch (error) {
                 console.error('Error uploading to Supabase during final URL processing:', error);
               } finally {
                 // Always reset generation state, even if there was an error
                 console.log('Resetting generation state after final URL processing');
                 setIsGenerating(false);
                 setProgress(0);
               }
             })();
          } else if (currentRunId) {
              // If we still don't have a URL but we have a run ID, try to fetch it directly
              console.log('No URL found in outputs, attempting direct fetch with run ID:', currentRunId);
              try {
                  const response = await fetch(`https://api.comfydeploy.com/api/v1/runs/${currentRunId}`, {
                      headers: {
                          'Authorization': `Bearer ${process.env.REACT_APP_COMFY_DEPLOY_SDK_API_KEY}`,
                          'Content-Type': 'application/json'
                      }
                  });
                  
                  if (response.ok) {
                      const runDetails = await response.json();
                      console.log('Run details retrieved directly:', runDetails);
                      
                      if (runDetails.status === 'success' && runDetails.outputs && runDetails.outputs.length > 0) {
                          const output = runDetails.outputs[0];
                          if (output.data?.images?.[0]?.url) {
                              const videoUrl = output.data.images[0].url;
                              console.log('Found video URL in direct API response:', videoUrl);
                              setGeneratedVideo(videoUrl);
                              videoUrlFound = true;
                          }
                      }
                  } else {
                      console.error('Failed to retrieve run details directly:', await response.text());
                  }
              } catch (directFetchError) {
                  console.error('Error fetching run details directly:', directFetchError);
              }
          }
          
          if (!videoUrlFound) {
              console.warn('No suitable video output found in workflow results or callbacks.');
              throw new Error('Video oluşturma tamamlandı ancak sonuçta video bulunamadı.'); // Turkish error
          }
      }

      // After workflow completes and we have a video URL, upload to Supabase
      if (videoUrlFound && generatedVideo) {
          const comfyDeployUrl = generatedVideo; // Store the initial URL (ComfyDeploy)
          console.log('Uploading video to Supabase from URL:', comfyDeployUrl);
          
          // Don't save the ComfyDeploy URL to the database as fallback
          // We'll only save the Supabase URL after successful upload
          
          try {
              // Fetch the video from ComfyDeploy
              const response = await fetch(comfyDeployUrl, {
                  method: 'GET',
                  cache: 'no-cache',
                  headers: {
                      'Accept': 'image/webp,image/*,*/*',
                  }
              });
              
              if (!response.ok) {
                  throw new Error(`Failed to fetch video from ComfyDeploy: ${response.status}`);
              }

              const blob = await response.blob();
              console.log('Video blob fetched successfully, size:', blob.size, 'type:', blob.type);
              
              if (blob.size === 0) {
                  throw new Error('Fetched blob has zero size');
              }
              
              const fileExtension = comfyDeployUrl.split('.').pop()?.toLowerCase() || 'webp';
              const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
              const contentType = fileExtension === 'webp' ? 'image/webp' : 'video/mp4';

              // Create a file object for upload
              const videoFile = new File([blob], fileName, {
                  type: contentType
              });

              // Upload to Supabase storage
              console.log(`Uploading to Supabase storage: ${fileName} (${videoFile.size} bytes)`);
              const { data: uploadData, error: uploadError } = await supabase.storage
                  .from('generated-videos')
                  .upload(`${session.user.id}/${fileName}`, videoFile, {
                      cacheControl: '3600',
                      upsert: true, // Changed to true to overwrite if exists
                      contentType: contentType
                  });

              if (uploadError) {
                  console.error('Error uploading video to Supabase storage:', uploadError);
                  // Try a second time with a different approach
                  console.log('Retrying upload with direct blob...');
                  const { data: retryData, error: retryError } = await supabase.storage
                      .from('generated-videos')
                      .upload(`${session.user.id}/retry-${fileName}`, blob, {
                          cacheControl: '3600',
                          upsert: true, // Changed to true
                          contentType: contentType
                      });
                      
                  if (retryError) {
                      console.error('Retry upload also failed:', retryError);
                      // Try one more approach - fetch and upload as base64
                      try {
                          console.log('Attempting final approach with base64 encoding');
                          const base64Response = await fetch(comfyDeployUrl);
                          const blob = await base64Response.blob();
                          const reader = new FileReader();
                      
                          // Convert blob to base64
                          const base64Promise = new Promise((resolve) => {
                              reader.onloadend = () => resolve(reader.result);
                              reader.readAsDataURL(blob);
                          });
                      
                          const base64Data = await base64Promise;
                          const finalFileName = `mimarai-video-base64-${Date.now()}.${fileExtension}`;
                      
                          // Save directly to database with base64 data
                          const videoRecord = {
                              id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2),
                              url: base64Data, // Store base64 data directly
                              user_id: session.user.id,
                              title: `İç Mekan Video ${new Date().toLocaleDateString('tr-TR')}`,
                              source_image_url: null,
                              camera_movement: cameraMovement,
                              duration: parseInt(duration, 10),
                              quality: quality,
                              created_at: new Date().toISOString(),
                              type: 'interior_video'
                          };
                      
                          const { error: insertError } = await supabase
                              .from('videos')
                              .insert([videoRecord]);
                          
                          if (insertError) {
                              console.error('Error saving base64 video to gallery:', insertError);
                          } else {
                              console.log('Saved base64 video directly to database');
                              // Keep ComfyDeploy URL in UI but database has base64
                          }
                      } catch (base64Error) {
                          console.error('Final base64 approach also failed:', base64Error);
                      }
                  } else {
                      // Get the public URL for the uploaded video
                      const { data: urlData } = supabase.storage
                          .from('generated-videos')
                          .getPublicUrl(retryData.path);
                          
                      if (urlData?.publicUrl) {
                          console.log('Retry upload successful, Supabase URL:', urlData.publicUrl);
                          setGeneratedVideo(urlData.publicUrl);
                          // Update metadata with the Supabase URL
                          await saveVideoMetadata(urlData.publicUrl);
                      } else {
                          console.warn('Retry upload succeeded but failed to get public URL');
                          // We already saved metadata with original URL as fallback
                      }
                  }
              } else {
                  // Get the public URL for the uploaded video
                  const { data: urlData } = supabase.storage
                      .from('generated-videos')
                      .getPublicUrl(uploadData.path);

                  if (urlData?.publicUrl) {
                      console.log('Video uploaded to Supabase storage:', urlData.publicUrl);
                      // Use Supabase URL instead of ComfyDeploy URL
                      setGeneratedVideo(urlData.publicUrl); // Update state

                      // Save metadata with the Supabase URL
                      await saveVideoMetadata(urlData.publicUrl);
                  } else {
                      console.warn('Supabase upload succeeded but failed to get public URL. Path:', uploadData.path);
                      // Try to get the URL directly
                      const directUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/generated-videos/${uploadData.path}`;
                      console.log('Attempting to use direct URL:', directUrl);
                      setGeneratedVideo(directUrl);
                      await saveVideoMetadata(directUrl);
                  }
              }
          } catch (uploadError) {
              console.error('Error fetching/uploading video to Supabase:', uploadError);
              // Try direct upload to database with base64 encoding
              try {
                  console.log('Attempting direct base64 encoding after fetch/upload error');
                  const base64Response = await fetch(comfyDeployUrl);
                  const blob = await base64Response.blob();
                  const reader = new FileReader();
                  
                  // Convert blob to base64
                  const base64Promise = new Promise((resolve) => {
                      reader.onloadend = () => resolve(reader.result);
                      reader.readAsDataURL(blob);
                  });
                  
                  const base64Data = await base64Promise;
                  
                  // Save directly to database with base64 data
                  const videoRecord = {
                      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2),
                      url: base64Data, // Store base64 data directly
                      user_id: session.user.id,
                      title: `İç Mekan Video ${new Date().toLocaleDateString('tr-TR')}`,
                      source_image_url: null,
                      camera_movement: cameraMovement,
                      duration: parseInt(duration, 10),
                      quality: quality,
                      created_at: new Date().toISOString(),
                      type: 'interior_video'
                  };
                  
                  const { error: insertError } = await supabase
                      .from('videos')
                      .insert([videoRecord]);
                      
                  if (insertError) {
                      console.error('Error saving base64 video to gallery:', insertError);
                  } else {
                      console.log('Saved base64 video directly to database');
                      // Keep ComfyDeploy URL in UI but database has base64
                  }
              } catch (base64Error) {
                  console.error('Base64 encoding approach also failed:', base64Error);
              }
          }
      } else if (!videoUrlFound && currentRunId) {
          // If we don't have a video URL but we have a run ID, try to fetch it directly
          console.log('No video URL found, attempting to fetch from run ID:', currentRunId);
          try {
              const response = await fetch(`https://api.comfydeploy.com/api/v1/runs/${currentRunId}`, {
                  headers: {
                      'Authorization': `Bearer ${process.env.REACT_APP_COMFY_DEPLOY_SDK_API_KEY}`,
                      'Content-Type': 'application/json'
                  }
              });
              
              if (response.ok) {
                  const runDetails = await response.json();
                  console.log('Run details retrieved:', runDetails);
                  
                  let foundUrl = null;
                  
                  // Check for video URL in outputs
                  if (runDetails.outputs && runDetails.outputs.length > 0) {
                      for (const output of runDetails.outputs) {
                          if (output.data?.images?.[0]?.url) {
                              foundUrl = output.data.images[0].url;
                              console.log('Found image URL in run details:', foundUrl);
                              break;
                          } else if (output.data?.videos?.[0]?.url) {
                              foundUrl = output.data.videos[0].url;
                              console.log('Found video URL in run details:', foundUrl);
                              break;
                          } else if (output.url) {
                              foundUrl = output.url;
                              console.log('Found direct URL in run details:', foundUrl);
                              break;
                          }
                      }
                  }
                  
                  if (foundUrl) {
                      console.log('Found video URL in run details:', foundUrl);
                      setGeneratedVideo(foundUrl); // Set the URL immediately so user sees something
                      
                      // Now upload this to Supabase
                      try {
                          const response = await fetch(foundUrl);
                          if (!response.ok) {
                              throw new Error(`Failed to fetch video from found URL: ${response.status}`);
                          }
                          
                          const blob = await response.blob();
                          console.log('Video blob fetched successfully from run details, size:', blob.size);
                          const fileExtension = foundUrl.split('.').pop()?.toLowerCase() || 'webp';
                          const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
                          
                          const videoFile = new File([blob], fileName, {
                              type: fileExtension === 'webp' ? 'image/webp' : 'video/mp4'
                          });
                          
                          console.log(`Uploading to Supabase storage from run details: ${fileName}`);
                          const { data: uploadData, error: uploadError } = await supabase.storage
                              .from('generated-videos')
                              .upload(`${session.user.id}/${fileName}`, videoFile, {
                                  cacheControl: '3600',
                                  upsert: false
                              });
                              
                          if (uploadError) {
                              console.error('Error uploading found video to Supabase:', uploadError);
                              await saveVideoMetadata(foundUrl);
                          } else {
                              const { data: urlData } = supabase.storage
                                  .from('generated-videos')
                                  .getPublicUrl(uploadData.path);
                                  
                              if (urlData?.publicUrl) {
                                  console.log('Found video uploaded to Supabase:', urlData.publicUrl);
                                  setGeneratedVideo(urlData.publicUrl);
                                  await saveVideoMetadata(urlData.publicUrl);
                              } else {
                                  console.warn('Upload succeeded but failed to get public URL');
                                  await saveVideoMetadata(foundUrl);
                              }
                          }
                      } catch (fetchError) {
                          console.error('Error fetching or uploading video from found URL:', fetchError);
                          await saveVideoMetadata(foundUrl);
                      } finally {
                          // Always reset generation state
                          console.log('Resetting generation state after run ID processing');
                          setIsGenerating(false);
                          setProgress(0);
                      }
                  } else {
                      console.warn('No video URL found in run details');
                  }
              } else {
                  console.error('Failed to retrieve run details:', await response.text());
              }
          } catch (error) {
              console.error('Error fetching run details or uploading found video:', error);
          }
      }

    } catch (error) {
      console.error('Error during video generation process:', error, 'Here is the comfysdk logs:', {id: currentRunId});
      
      // Reset generation states immediately
      setIsGenerating(false);
      setProgress(0);
      
      // Try to fetch the run details directly if we have a run ID
      if (currentRunId) {
        try {
          // Try direct API call to ComfyDeploy
          const runDetailsResponse = await fetch(`https://api.comfydeploy.com/api/v1/runs/${currentRunId}`, {
            headers: {
              'Authorization': `Bearer ${process.env.REACT_APP_COMFY_DEPLOY_SDK_API_KEY}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (runDetailsResponse.ok) {
            const runDetails = await runDetailsResponse.json();
            console.log('Run details retrieved directly from ComfyDeploy API:', runDetails);
            
            // Check if the run was actually successful despite the error
            if (runDetails.status === 'success' && runDetails.outputs && runDetails.outputs.length > 0) {
              // Try to extract the video URL from the outputs
              const output = runDetails.outputs[0];
              if (output.data?.images?.[0]?.url) {
                const videoUrl = output.data.images[0].url;
                console.log('Found video URL in run details:', videoUrl);
                setGeneratedVideo(videoUrl);
                setProgress(100);
                setIsGenerating(false);
                return; // Exit early since we recovered
              }
            }
          } else {
            console.error('Failed to retrieve run details from ComfyDeploy API:', await runDetailsResponse.text());
            
            // Fallback to your API if direct call fails
            try {
              const backupResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/comfy-deploy/run/${currentRunId}`, {
                headers: {
                  'Authorization': `Bearer ${session.access_token}`
                }
              });
              
              if (backupResponse.ok) {
                const backupDetails = await backupResponse.json();
                console.log('Run details retrieved from backup API:', backupDetails);
                
                if (backupDetails.status === 'success' && backupDetails.outputs && backupDetails.outputs.length > 0) {
                  const output = backupDetails.outputs[0];
                  if (output.data?.images?.[0]?.url) {
                    const videoUrl = output.data.images[0].url;
                    console.log('Found video URL in backup API response:', videoUrl);
                    setGeneratedVideo(videoUrl);
                    setProgress(100);
                    setIsGenerating(false);
                    return; // Exit early since we recovered
                  }
                }
              } else {
                console.error('Failed to retrieve run details from backup API:', await backupResponse.text());
              }
            } catch (backupError) {
              console.error('Error fetching run details from backup API:', backupError);
            }
          }
        } catch (detailsError) {
          console.error('Error fetching run details:', detailsError);
        }
      }
      
      // Set error state for user feedback
      // Check if it's an AbortError from the timeout
      if (error.name === 'AbortError') {
          setError(error.message); // Use the timeout message
      } else {
          setError(error.message || 'Video oluşturma başarısız oldu. Lütfen tekrar deneyin.'); // Generic Turkish error
      }
      // Ensure video state is cleared on error
      setGeneratedVideo(null);
    } finally {
      // Reset states immediately
      setIsGenerating(false);
      setProgress(0); // Always reset progress after generation attempt
      
      // Small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // <<< The upload/save block below was moved inside the 'try' block >>>

  };

  // Handle video playback
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          // Autoplay might be blocked, handle error gracefully
          console.error("Video play failed:", error);
          setIsPlaying(false); // Ensure state reflects reality
          setError("Tarayıcı ayarları nedeniyle video otomatik oynatılamadı. Oynat düğmesini kullanın."); // Inform user
        });
      }
      // State will be updated by the event listeners below
    }
  };

  // Handle video download
  const handleDownload = async () => {
    if (generatedVideo) {
      try {
          console.log('Downloading video from URL:', generatedVideo);
          
          // Don't save ComfyDeploy URLs directly to database
          // We'll upload to Supabase first and then save the Supabase URL
          
          // Fetch the video as a blob to handle potential CORS issues or protected URLs
          const response = await fetch(generatedVideo, {
              method: 'GET',
              cache: 'no-cache',
              headers: {
                  'Accept': 'image/webp,image/*,*/*',
              }
          });
          
          if (!response.ok) {
              throw new Error(`Video dosyası indirilemedi: Sunucu hatası ${response.status}`);
          }
          
          const blob = await response.blob();
          console.log('Video blob fetched successfully, size:', blob.size, 'type:', blob.type);
          
          if (blob.size === 0) {
              throw new Error('Fetched blob has zero size');
          }
          
          const objectUrl = window.URL.createObjectURL(blob);
          const fileExtension = generatedVideo.split('.').pop()?.toLowerCase() || 'webp';
          const downloadFileName = `mimarai-video-${Date.now()}.${fileExtension}`;
          const contentType = fileExtension === 'webp' ? 'image/webp' : 'video/mp4';

          // Create a temporary link to trigger the download
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = downloadFileName;
          console.log(`Triggering download with filename: ${downloadFileName}`);
          document.body.appendChild(link);
          link.click();

          // Clean up
          // Check if the link is still a child before removing
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          // Delay revokeObjectURL slightly to ensure download starts
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 100);

          // Save to user's gallery if they're logged in
          if (session?.user?.id) {
            try {
              // Check if the URL is already a Supabase URL
              const isSupabaseUrl = generatedVideo.includes('supabase') || 
                                   generatedVideo.includes('generated-videos');
              
              if (isSupabaseUrl) {
                // If it's already a Supabase URL, just save the metadata
                console.log('Using existing Supabase URL for gallery:', generatedVideo);
                await saveVideoMetadata(generatedVideo);
              } else {
                // If not a Supabase URL, upload to Supabase storage
                console.log('Uploading video to Supabase storage before saving to gallery');
                // Convert blob to file for upload
                const videoFile = new File([blob], downloadFileName, { 
                  type: contentType
                });
                
                // Upload to Supabase storage
                console.log(`Uploading to Supabase storage: ${downloadFileName} (${videoFile.size} bytes)`);
                const { data: uploadData, error: uploadError } = await supabase.storage
                  .from('generated-videos')
                  .upload(`${session.user.id}/${videoFile.name}`, videoFile, {
                    cacheControl: '3600',
                    upsert: true, // Changed to true
                    contentType: contentType
                  });
                  
                if (uploadError) {
                  console.error('Error uploading video to Supabase storage:', uploadError);
                  // Try direct blob upload
                  console.log('Retrying with direct blob upload');
                  const retryFileName = `mimarai-video-retry-${Date.now()}.${fileExtension}`;
                  
                  const { data: retryData, error: retryError } = await supabase.storage
                    .from('generated-videos')
                    .upload(`${session.user.id}/${retryFileName}`, blob, {
                      cacheControl: '3600',
                      upsert: true, // Changed to true
                      contentType: contentType
                    });
                    
                  if (retryError) {
                    console.error('Retry upload also failed:', retryError);
                    // Try base64 approach as last resort
                    try {
                        console.log('Attempting base64 encoding for download');
                        const base64Reader = new FileReader();
                        
                        const base64Promise = new Promise((resolve) => {
                            base64Reader.onloadend = () => resolve(base64Reader.result);
                            base64Reader.readAsDataURL(blob);
                        });
                        
                        const base64Data = await base64Promise;
                        
                        // Save directly to database with base64 data
                        const videoRecord = {
                            id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2),
                            url: base64Data, // Store base64 data directly
                            user_id: session.user.id,
                            title: `İç Mekan Video ${new Date().toLocaleDateString('tr-TR')}`,
                            source_image_url: null,
                            camera_movement: cameraMovement,
                            duration: parseInt(duration, 10),
                            quality: quality,
                            created_at: new Date().toISOString(),
                            type: 'interior_video'
                        };
                        
                        const { error: insertError } = await supabase
                            .from('videos')
                            .insert([videoRecord]);
                            
                        if (insertError) {
                            console.error('Error saving base64 video to gallery:', insertError);
                        } else {
                            console.log('Saved base64 video directly to database during download');
                        }
                    } catch (base64Error) {
                        console.error('Base64 encoding during download failed:', base64Error);
                    }
                  } else {
                    // Get the public URL for the uploaded video
                    const { data: urlData } = supabase.storage
                      .from('generated-videos')
                      .getPublicUrl(retryData.path);
                      
                    if (urlData?.publicUrl) {
                      console.log('Video uploaded to Supabase storage on retry:', urlData.publicUrl);
                      // Update the UI with the Supabase URL
                      setGeneratedVideo(urlData.publicUrl);
                      // Update metadata with the Supabase URL
                      await saveVideoMetadata(urlData.publicUrl);
                    } else {
                      console.warn('Retry upload succeeded but failed to get public URL');
                      // We already saved metadata with original URL as fallback
                    }
                  }
                } else {
                  // Get the public URL for the uploaded video
                  const { data: urlData } = supabase.storage
                    .from('generated-videos')
                    .getPublicUrl(uploadData.path);
                    
                  if (urlData?.publicUrl) {
                    console.log('Video uploaded to Supabase storage:', urlData.publicUrl);
                    // Update the UI with the Supabase URL
                    setGeneratedVideo(urlData.publicUrl);
                    // Update metadata with the Supabase URL
                    await saveVideoMetadata(urlData.publicUrl);
                  } else {
                    console.warn('Supabase upload succeeded but failed to get public URL');
                    // We already saved metadata with original URL as fallback
                  }
                }
              }
            } catch (galleryError) {
              console.error('Exception during gallery save:', galleryError);
              // We already saved metadata with original URL as fallback
              console.log('Using fallback metadata with original URL');
            }
          }
      } catch (error) {
          console.error('Error downloading video:', error);
          setError(`Video indirilemedi: ${error.message}`);
          
          // We already saved metadata with original URL as fallback
          console.log('Using fallback metadata with original URL');
      }
    }
  };
  
  // Helper function to upload source image and save video metadata
  const saveVideoMetadata = async (videoUrl, skipUpload = false) => {
    console.log('Saving video metadata for URL:', videoUrl);
    if (!session?.user?.id) {
      console.error('Cannot save video metadata: No user session');
      return;
    }
    
    // Always check if this is a ComfyDeploy URL and upload to Supabase
    if ((
        videoUrl.includes('comfydeploy.com') || 
        videoUrl.includes('comfy.xyz') || 
        videoUrl.includes('replicate.delivery') ||
        videoUrl.includes('s3.us-east-2.amazonaws.com')
    )) {
      console.log('ComfyDeploy URL detected, uploading to Supabase:', videoUrl);
      
      // If skipUpload is true, we'll still save the metadata but with a warning
      if (skipUpload) {
        console.warn('skipUpload is true, but this is a ComfyDeploy URL. Saving anyway, but this should be avoided.');
      } else {
        try {
          const response = await fetch(videoUrl, {
            method: 'GET',
            cache: 'no-cache',
            headers: {
              'Accept': 'image/webp,image/*,*/*',
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch from ComfyDeploy: ${response.status}`);
          }
          
          const blob = await response.blob();
          if (blob.size === 0) {
            throw new Error('Fetched blob has zero size');
          }
          
          const fileExtension = videoUrl.split('.').pop()?.toLowerCase() || 'webp';
          const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
          const contentType = fileExtension === 'webp' ? 'image/webp' : 'video/mp4';
          
          const videoFile = new File([blob], fileName, { type: contentType });
          
          console.log(`Uploading ComfyDeploy video to Supabase: ${fileName} (${videoFile.size} bytes)`);
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('generated-videos')
            .upload(`${session.user.id}/${fileName}`, videoFile, {
              cacheControl: '3600',
              upsert: true,
              contentType: contentType
            });
            
          if (uploadError) {
            throw uploadError;
          }
          
          const { data: urlData } = supabase.storage
            .from('generated-videos')
            .getPublicUrl(uploadData.path);
            
          if (!urlData?.publicUrl) {
            throw new Error('Failed to get public URL after upload');
          }
          
          console.log('Successfully uploaded to Supabase, using new URL:', urlData.publicUrl);
          // Update UI with Supabase URL
          setGeneratedVideo(urlData.publicUrl);
          // Continue with the Supabase URL
          videoUrl = urlData.publicUrl;
        } catch (error) {
          console.error('Failed to upload ComfyDeploy URL to Supabase:', error);
          // Try base64 approach as last resort
          try {
            console.log('Attempting base64 encoding for database storage');
            const base64Response = await fetch(videoUrl);
            const blob = await base64Response.blob();
            const reader = new FileReader();
            
            const base64Promise = new Promise((resolve) => {
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            
            const base64Data = await base64Promise;
            videoUrl = base64Data; // Use base64 data as URL
            console.log('Successfully converted to base64 for storage');
          } catch (base64Error) {
            console.error('Base64 encoding also failed:', base64Error);
            console.warn('Continuing with ComfyDeploy URL as last resort, but this should be avoided');
          }
        }
      }
    }
    
    let persistentSourceImageUrl = null;

    // 1. Upload source image to storage if it exists and is a blob URL
    if (uploadedImage && previewUrl && previewUrl.startsWith('blob:') && !skipUpload) {
      try {
        const fileExt = uploadedImage.name.split('.').pop()?.toLowerCase();
        const fileName = `video-source-${session.user.id}-${Date.now()}.${fileExt}`;
        console.log(`Uploading source image: ${fileName}`);
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('uploaded-images') // Use the same bucket as video inputs or a dedicated one
          .upload(fileName, uploadedImage, {
            cacheControl: '3600',
            upsert: true // Changed to true
          });

        if (uploadError) {
          console.error('Error uploading source image:', uploadError);
          // Try a different approach
          try {
            console.log('Retrying source image upload with blob approach');
            // Convert File to Blob if needed
            const blob = await uploadedImage.arrayBuffer().then(buffer => new Blob([buffer], { type: uploadedImage.type }));
            const retryFileName = `video-source-retry-${session.user.id}-${Date.now()}.${fileExt}`;
            
            const { data: retryData, error: retryError } = await supabase.storage
              .from('uploaded-images')
              .upload(retryFileName, blob, {
                cacheControl: '3600',
                upsert: true, // Changed to true
                contentType: uploadedImage.type
              });
              
            if (retryError) {
              console.error('Retry source image upload also failed:', retryError);
            } else if (retryData?.path) {
              const { data: urlData } = supabase.storage
                .from('uploaded-images')
                .getPublicUrl(retryData.path);
              persistentSourceImageUrl = urlData?.publicUrl;
              console.log('Source image uploaded on retry:', persistentSourceImageUrl);
            }
          } catch (retryError) {
            console.error('Exception during retry source image upload:', retryError);
          }
        } else if (uploadData?.path) {
          const { data: urlData } = supabase.storage
            .from('uploaded-images')
            .getPublicUrl(uploadData.path);
          persistentSourceImageUrl = urlData?.publicUrl;
          console.log('Source image uploaded:', persistentSourceImageUrl);
        }
      } catch (error) {
        console.error('Exception uploading source image:', error);
      }
    } else if (previewUrl && !previewUrl.startsWith('blob:')) {
      // If previewUrl is already a persistent URL (e.g., from asset library), use it
      persistentSourceImageUrl = previewUrl;
      console.log('Using existing persistent source image URL:', persistentSourceImageUrl);
    }

    // 2. Save video metadata to Supabase 'videos' table
    try {
      // Check if the URL is from ComfyDeploy and not already a Supabase URL
      const isComfyDeployUrl = videoUrl.includes('comfydeploy.com') || 
                              videoUrl.includes('comfy.xyz') || 
                              videoUrl.includes('replicate.delivery') ||
                              videoUrl.includes('s3.us-east-2.amazonaws.com');
      
      let finalVideoUrl = videoUrl;
      
      // If it's a ComfyDeploy URL and we're not skipping upload, try to upload it to Supabase first
      if (isComfyDeployUrl && !skipUpload) {
        try {
          console.log('Uploading ComfyDeploy video to Supabase:', videoUrl);
          const response = await fetch(videoUrl, {
              method: 'GET',
              cache: 'no-cache',
              headers: {
                  'Accept': 'image/webp,image/*,*/*',
              }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            console.log('ComfyDeploy video blob fetched, size:', blob.size, 'type:', blob.type);
            
            if (blob.size === 0) {
                throw new Error('Fetched blob has zero size');
            }
            
            const fileExtension = videoUrl.split('.').pop()?.toLowerCase() || 'webp';
            const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
            const contentType = fileExtension === 'webp' ? 'image/webp' : 'video/mp4';
            
            const videoFile = new File([blob], fileName, {
              type: contentType
            });
            
            console.log(`Uploading ComfyDeploy video to Supabase: ${fileName}`);
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('generated-videos')
              .upload(`${session.user.id}/${fileName}`, videoFile, {
                cacheControl: '3600',
                upsert: true, // Changed to true
                contentType: contentType
              });
              
            if (uploadError) {
              console.error('Error uploading ComfyDeploy video to Supabase:', uploadError);
              // Try direct blob upload
              console.log('Retrying with direct blob upload');
              const retryFileName = `mimarai-video-retry-${Date.now()}.${fileExtension}`;
              
              const { data: retryData, error: retryError } = await supabase.storage
                .from('generated-videos')
                .upload(`${session.user.id}/${retryFileName}`, blob, {
                  cacheControl: '3600',
                  upsert: true, // Changed to true
                  contentType: contentType
                });
                
              if (!retryError && retryData?.path) {
                const { data: urlData } = supabase.storage
                  .from('generated-videos')
                  .getPublicUrl(retryData.path);
                  
                if (urlData?.publicUrl) {
                  console.log('ComfyDeploy video uploaded to Supabase on retry:', urlData.publicUrl);
                  finalVideoUrl = urlData.publicUrl;
                  // Update the UI with the Supabase URL
                  setGeneratedVideo(finalVideoUrl);
                }
              } else {
                console.error('Retry upload also failed:', retryError);
              }
            } else if (uploadData?.path) {
              const { data: urlData } = supabase.storage
                .from('generated-videos')
                .getPublicUrl(uploadData.path);
                
              if (urlData?.publicUrl) {
                console.log('ComfyDeploy video uploaded to Supabase:', urlData.publicUrl);
                finalVideoUrl = urlData.publicUrl;
                // Update the UI with the Supabase URL
                setGeneratedVideo(finalVideoUrl);
              }
            }
          } else {
            console.error('Failed to fetch ComfyDeploy video:', response.status);
          }
        } catch (uploadError) {
          console.error('Error uploading ComfyDeploy video to Supabase:', uploadError);
          // Continue with original URL if upload fails
        }
      }

      // Generate a UUID for the video record
      const videoId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2);
      
      console.log('Inserting video record into database with URL:', finalVideoUrl);
      const videoRecord = {
        id: videoId, // Use generated UUID
        url: finalVideoUrl, // Store the URL (either original or Supabase)
        user_id: session.user.id,
        title: `İç Mekan Video ${new Date().toLocaleDateString('tr-TR')}`,
        source_image_url: persistentSourceImageUrl, // Store the PERSISTENT source image URL
        camera_movement: cameraMovement,
        duration: parseInt(duration, 10),
        quality: quality,
        created_at: new Date().toISOString(),
        type: 'interior_video'
      };
      
      console.log('Video record to insert:', videoRecord);
      
      // Skip RPC call since it doesn't exist in the database
      // Go directly to insert approach
      
      // Direct insert approach
      const { data: insertData, error: insertError } = await supabase
        .from('videos')
        .insert([videoRecord]);

      if (insertError) {
        console.error('Error saving video to gallery DB:', insertError);
        // Try one more time with a simplified record
        try {
          console.log('Retrying with simplified record');
          const simplifiedRecord = {
            id: videoId, // Keep same ID
            url: finalVideoUrl,
            user_id: session.user.id,
            title: `İç Mekan Video ${Date.now()}`,
            created_at: new Date().toISOString(),
            type: 'interior_video'
          };
          
          console.log('Simplified record:', simplifiedRecord);
          
          const { data: retryData, error: retryError } = await supabase
            .from('videos')
            .insert([simplifiedRecord]);
            
          if (retryError) {
            console.error('Retry error saving video to gallery:', retryError);
            
            // Last attempt with minimal fields
            console.log('Final attempt with minimal fields');
            const minimalRecord = {
              id: videoId, // Keep same ID
              url: finalVideoUrl,
              user_id: session.user.id,
              created_at: new Date().toISOString()
            };
            
            // Try upsert as last resort
            const { error: finalError } = await supabase
              .from('videos')
              .upsert([minimalRecord]);
              
            if (finalError) {
              console.error('Final attempt also failed:', finalError);
              
              // Absolute last resort - try one more time with minimal fields
              try {
                const minimalRecord = {
                  id: videoId,
                  url: finalVideoUrl,
                  user_id: session.user.id,
                  created_at: new Date().toISOString()
                };
                
                const { error: finalInsertError } = await supabase
                  .from('videos')
                  .insert([minimalRecord]);
                  
                if (finalInsertError) {
                  console.error('Final insert attempt also failed:', finalInsertError);
                } else {
                  console.log('Video saved with minimal fields on final attempt');
                }
              } catch (finalException) {
                console.error('Exception during final insert attempt:', finalException);
              }
            } else {
              console.log('Video saved to gallery on final attempt');
            }
          } else {
            console.log('Video saved to gallery on retry:', retryData);
          }
        } catch (retryException) {
          console.error('Exception during retry save:', retryException);
        }
      } else {
        console.log('Video saved to gallery successfully:', insertData);
      }
    } catch (error) {
      console.error('Exception during metadata save:', error);
    }
  };

   // Handle Share action
  const handleShare = async () => {
    if (!generatedVideo) return;

    const shareData = {
      title: 'MimarAI İç Mekan Videosu',
      text: 'MimarAI ile oluşturduğum bu iç mekan videosuna göz atın!',
      url: generatedVideo,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        console.log('Video başarıyla paylaşıldı');
      } else if (navigator.clipboard) {
        // Fallback for browsers without Web Share API
        await navigator.clipboard.writeText(generatedVideo);
        alert('Video bağlantısı panoya kopyalandı!'); // Simple alert feedback
      } else {
          alert('Tarayıcınız paylaşımı veya panoya kopyalamayı desteklemiyor.');
      }
    } catch (err) {
      console.error('Paylaşım/Kopyalama hatası:', err);
      // Avoid alerting if user cancelled share dialog ('AbortError')
      if (err.name !== 'AbortError') {
         setError(`Paylaşım sırasında hata: ${err.message}`);
      }
    }
  };


  // Toggle settings panel
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  // Studio mode functions
  const addKeyframe = (time) => {
    const newKeyframe = {
      id: Date.now(),
      time,
      properties: {
        position: { x: 0, y: 0 },
        zoom: 1,
        rotation: 0
      }
    };
    
    setKeyframes([...keyframes, newKeyframe]);
    setSelectedKeyframe(newKeyframe.id);
  };
  
  const updateKeyframe = (id, properties) => {
    setKeyframes(keyframes.map(kf => 
      kf.id === id ? { ...kf, properties: { ...kf.properties, ...properties } } : kf
    ));
  };
  
  const deleteKeyframe = (id) => {
    setKeyframes(keyframes.filter(kf => kf.id !== id));
    if (selectedKeyframe === id) {
      setSelectedKeyframe(null);
    }
  };

  const addAssetToLibrary = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      console.warn('Invalid file type for asset library');
      return null;
    }

    try {
      const uniqueId = Date.now() + '-' + Math.floor(Math.random() * 10000);
      let objectUrl = null; // Initialize objectUrl

      // Check if this file is already in the library by comparing name and size
      const existingAssetIndex = assetLibrary.findIndex(asset =>
        asset.name === file.name &&
        asset.size === file.size
      );

      if (existingAssetIndex !== -1) {
        const existingAsset = assetLibrary[existingAssetIndex];
        console.log('Asset already exists in library:', existingAsset.name);

        // Check if the existing URL is still valid (simple check: starts with blob:)
        // A more robust check would involve trying to fetch it, but that's complex.
        // Let's assume if it starts with blob:, it *might* be valid.
        // If the URL is missing or doesn't look like a blob URL, create a new one.
        if (!existingAsset.url || !existingAsset.url.startsWith('blob:')) {
            console.log('Existing asset URL seems invalid, creating a new one.');
            objectUrl = URL.createObjectURL(file);
            // Update the existing asset's URL in the library
            setAssetLibrary(prevLibrary => {
                const updatedLibrary = [...prevLibrary];
                updatedLibrary[existingAssetIndex] = { ...existingAsset, url: objectUrl, file: file }; // Update URL and file ref
                return updatedLibrary;
            });
        } else {
            // Assume existing URL is okay for now. Select it.
            objectUrl = existingAsset.url; // Use the existing URL
        }
        selectAsset(existingAsset.id);
        // If we created a new URL, the state update above handles it.
        // If we used the existing URL, no further action needed here.
        return { ...existingAsset, url: objectUrl }; // Return the potentially updated existing asset
      }

      // If asset does not exist, create a new one
      objectUrl = URL.createObjectURL(file); // Create URL for the new asset

      const newAsset = {
        id: uniqueId,
        name: file.name,
        type: file.type,
        size: file.size,
        url: objectUrl,
        file: file, // Store the file object itself
        dateAdded: new Date().toISOString()
      };

      setAssetLibrary(prevLibrary => [...prevLibrary, newAsset]);
      selectAsset(newAsset.id);
      console.log('Added new asset to library:', newAsset.name);

      if (!uploadedImage) {
        setUploadedImage(file);
        setPreviewUrl(objectUrl);
      }

      return newAsset;
    } catch (error) {
      console.error('Error adding asset to library:', error);
      setError('Görsel kütüphaneye eklenirken bir hata oluştu.');
      return null;
    }
  };

  const selectAsset = (assetId) => {
    setSelectedAsset(assetId);

    // If selecting an asset from the library, update the main image
    if (assetId) {
      const asset = assetLibrary.find(a => a.id === assetId);
      if (asset && asset.file) {
        // Update the main image with this asset
        setUploadedImage(asset.file);
        // Ensure the preview URL is also updated to the asset's URL
        // This might be crucial if the asset's URL was updated (e.g., in addAssetToLibrary)
        if (asset.url) {
            setPreviewUrl(asset.url);
        } else {
            // Fallback if URL is missing for some reason
            console.warn(`Asset ${assetId} is missing a URL.`);
            // Optionally try to recreate it, but that might be complex here.
            // For now, clear the preview if URL is missing.
            setPreviewUrl(null);
        }
      } else if (assetId === 'main' && uploadedImage) {
          // Handle selecting the 'main' pseudo-asset if needed
          // This case might not be used anymore with the new logic
          // Let's ensure previewUrl is set correctly if the main image exists
          const mainAsset = assetLibrary.find(a => a.file === uploadedImage);
          if (mainAsset && mainAsset.url) {
              setPreviewUrl(mainAsset.url);
          } else if (previewUrl && previewUrl.startsWith('blob:')) {
              // Keep existing previewUrl if it's a blob and no matching asset found
          } else if (uploadedImage) {
              // Try to recreate URL if previewUrl is lost
              try {
                  const newUrl = URL.createObjectURL(uploadedImage);
                  setPreviewUrl(newUrl);
              } catch (e) { setPreviewUrl(null); }
          } else {
              setPreviewUrl(null);
          }
      }
    } else {
        // No asset selected (or 'main' selected without an uploadedImage)
        setSelectedAsset(null);
        // Optionally clear the main image/preview, or leave as is?
        // Let's leave them as is for now.
    }
  };
  
  // Handle timeline item drag
  const handleTimelineItemDrag = (outputId, newStartTime) => {
    setVideoOutputs(videoOutputs.map(output => {
      if (output.id === outputId) {
        const duration = output.endTime - output.startTime;
        const adjustedStartTime = Math.max(0, Math.min(newStartTime, totalDuration - duration));
        return {
          ...output,
          startTime: adjustedStartTime,
          endTime: adjustedStartTime + duration
        };
      }
      return output;
    }));
  };
  
  // Function to generate a specific video output from the timeline
  const handleGenerateVideoOutput = async (outputId) => {
    console.log('handleGenerateVideoOutput called for output:', outputId);
    
    // Reset progress when starting new generation
    setProgress(0);
    
    // Get current outputs using functional update to ensure we have latest state
    let currentOutputs;
    await new Promise(resolve => {
      setVideoOutputs(prevOutputs => {
        currentOutputs = prevOutputs;
        resolve();
        return prevOutputs;
      });
    });
    
    // Check if we're already generating a video
    const isGeneratingAny = currentOutputs.some(o => o.status === 'generating');
    if (isGeneratingAny) {
      setError('Lütfen mevcut video oluşturma işleminin tamamlanmasını bekleyin.');
      return;
    }

    // Check and consume credits before generation
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error('Failed to authenticate user');
      
      const requiredCredits = 30; // Fixed 30 credits per video
      const { error: creditError } = await consumeCredits(user.id, requiredCredits);
      
      if (creditError) {
        throw new Error(creditError.message || 'Kredi yetersiz veya işlem başarısız oldu. Lütfen kontrol edin.');
      }
    } catch (error) {
      console.error('Credit consumption error:', error);
      setError(error.message);
      setIsGenerating(false);
      setProgress(0);
      
      // Redirect to subscription page if credits are insufficient
      if (error.message.includes('Insufficient credits')) {
        window.location.href = '/subscription';
      }
      return;
    }

    // Check and consume credits before generation
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error('Failed to authenticate user');
      
      const requiredCredits = 30; // Fixed 30 credits per video
      const { error: creditError } = await consumeCredits(user.id, requiredCredits);
      
      if (creditError) {
        throw new Error(creditError.message || 'Kredi yetersiz veya işlem başarısız oldu. Lütfen kontrol edin.');
      }
    } catch (error) {
      console.error('Credit consumption error:', error);
      setError(error.message);
      setIsGenerating(false);
      setProgress(0);
      
      // Reset any outputs that were set to 'generating'
      setVideoOutputs(prevOutputs => 
        prevOutputs.map(o => 
          o.id === outputId ? { ...o, status: 'pending' } : o
        )
      );
      
      // Redirect to subscription page if credits are insufficient
      if (error.message.includes('Insufficient credits')) {
        window.location.href = '/subscription';
      }
      return;
    }
    
    // Find the output in the current outputs
    const output = currentOutputs.find(o => o.id === outputId);
    if (!output) {
      console.error('Output not found in current outputs:', outputId, 'Available outputs:', currentOutputs);
      setError('Video çıktısı bulunamadı. Lütfen tekrar deneyin.');
      
      // Clean up any references to this output
      setVideoOutputs(prevOutputs => prevOutputs.filter(o => o.id !== outputId));
      if (selectedOutput === outputId) {
        setSelectedOutput(null);
      }
      return;
    }
    
    // Update status to generating using functional update to prevent race conditions
    setVideoOutputs(prevOutputs => 
      prevOutputs.map(o => 
        o.id === outputId ? { ...o, status: 'generating', generationStarted: new Date().toISOString() } : o
      )
    );
    
    // Reset progress to ensure UI shows the correct state
    setProgress(0);
    
    // Force a state update cycle to ensure the UI reflects the generating state
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      // Prepare the specific settings for this output
      const outputSettings = {
        ...output.settings,
        duration: Math.round(output.endTime - output.startTime),
        input_image: null // Will be set in handleGenerateVideo
      };
      
      // Call the main generate function with these settings
      const videoUrl = await handleGenerateVideoWithSettings(outputSettings);
      
      // Verify the output still exists (user might have deleted it during generation)
      const currentOutputs = await new Promise(resolve => {
        setVideoOutputs(prevOutputs => {
          resolve(prevOutputs);
          return prevOutputs;
        });
      });
      
      const outputStillExists = currentOutputs.some(o => o.id === outputId);
      if (!outputStillExists) {
        console.log('Output was deleted during generation, not updating state');
        setError('Video oluşturma sırasında çıktı silindi.');
        setIsGenerating(false);
        setProgress(0);
        return;
      }
      
      // Update the output with the generated video URL using functional update
      setVideoOutputs(prevOutputs => 
        prevOutputs.map(o => 
          o.id === outputId ? { 
            ...o, 
            status: 'completed',
            url: videoUrl,
            generationCompleted: new Date().toISOString()
          } : o
        )
      );
      
      // Show the generated video
      setGeneratedVideo(videoUrl);
      setSelectedOutput(outputId);
      
      // Reset generation states immediately
      setIsGenerating(false);
      setProgress(0);
      
      // Small delay to ensure UI updates before showing success
      await new Promise(resolve => setTimeout(resolve, 300));
      
    } catch (error) {
      console.error('Error generating video output:', error);
      
      // Reset generation states immediately
      setIsGenerating(false);
      setProgress(0);
      setError(error.message || 'Video oluşturma hatası');
      
      // Small delay to ensure UI updates before showing error
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Get current outputs to check existence
      const currentOutputs = await new Promise(resolve => {
        setVideoOutputs(prevOutputs => {
          resolve(prevOutputs);
          return prevOutputs;
        });
      });
      
      if (currentOutputs.some(o => o.id === outputId)) {
        // Update status to error using functional update
        setVideoOutputs(prevOutputs => 
          prevOutputs.map(o => 
            o.id === outputId ? { 
              ...o, 
              status: 'error',
              errorMessage: error.message || 'Video oluşturma hatası',
              generationFailed: new Date().toISOString()
            } : o
          )
        );
      }
    }
  };
  
  // Modified version of handleGenerateVideo that accepts custom settings
  const handleGenerateVideoWithSettings = async (customSettings) => {
    if (!uploadedImage || !session || !videoWorkflow) {
      throw new Error('Missing requirements for video generation');
    }
    
    console.log('handleGenerateVideoWithSettings called with settings:', customSettings);
    
    // Use component state for progress tracking
    let videoUrl = null;
    
    try {
      // 1. Upload to Supabase storage (same as original function)
      const fileExt = uploadedImage.name.split('.').pop()?.toLowerCase();
      const fileName = `video-input-${session.user.id}-${Date.now()}.${fileExt}`;
      
      console.log('Uploading image to Supabase:', fileName);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('uploaded-images')
        .upload(fileName, uploadedImage, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Resim yüklenemedi: ${uploadError.message}`);
      }
      if (!uploadData?.path) {
        throw new Error('Supabase upload succeeded but did not return a path.');
      }

      // 2. Get the public URL
      const { data: urlData } = supabase.storage
        .from('uploaded-images')
        .getPublicUrl(uploadData.path);

      if (!urlData?.publicUrl) {
        throw new Error('Yüklenen resmin genel URL\'si alınamadı.');
      }
      const publicUrl = urlData.publicUrl;
      
      console.log('Image uploaded successfully, public URL:', publicUrl);
      
      // 3. Prepare inputs using the custom settings
      let selectedImageUrl = publicUrl;
      
      // If a specific asset is selected in the settings
      if (customSettings.assetId && customSettings.assetId !== 'main') {
        // Find the selected asset in the asset library
        const selectedAssetObj = assetLibrary.find(asset => asset.id === customSettings.assetId);
        if (selectedAssetObj && selectedAssetObj.url) {
          // Upload this asset to Supabase
          try {
            const assetFile = selectedAssetObj.file;
            const fileExt = assetFile.name.split('.').pop()?.toLowerCase();
            const fileName = `video-input-${session.user.id}-${Date.now()}-${customSettings.assetId}.${fileExt}`;
            
            const { data: assetUploadData, error: assetUploadError } = await supabase.storage
              .from('uploaded-images')
              .upload(fileName, assetFile, {
                cacheControl: '3600',
                upsert: false
              });
              
            if (!assetUploadError && assetUploadData?.path) {
              // Get the public URL
              const { data: assetUrlData } = supabase.storage
                .from('uploaded-images')
                .getPublicUrl(assetUploadData.path);
                
              if (assetUrlData?.publicUrl) {
                selectedImageUrl = assetUrlData.publicUrl;
              }
            }
          } catch (assetError) {
            console.error('Error processing selected asset:', assetError);
            // Fall back to main image
          }
        }
      }
      
      // Merge default inputs with custom settings
      const inputs = {
        input_image: selectedImageUrl,
        camera_movement: customSettings.cameraMovement || cameraMovement,
        duration: customSettings.duration || parseInt(duration, 10),
        quality: customSettings.quality || quality,
        camera_path: customSettings.cameraPath || cameraPath,
        studio_mode: true
      };
      
      // 4. Run the workflow
      console.log('Running workflow with custom settings:', inputs);
      
      // Create a promise that will resolve with the video URL
      return new Promise((resolve, reject) => {
        runComfyDeployWorkflow(
          videoWorkflow.deploymentId,
          inputs,
          (progressValue, currentOutput) => {
            // Update UI progress state
            const newProgress = Math.min(progressValue * 100, 100);
            setProgress(newProgress);
            console.log('Setting progress:', newProgress);
                                    
            // Check for video URL in the output
            if (progressValue >= 0.95 && currentOutput?.url) {
              videoUrl = currentOutput.url;
            }
          }
        ).then(async (outputs) => {
          // Process outputs to find video URL
          let finalUrl = videoUrl; // Use URL from progress callback if available
          
          if (!finalUrl) {
            // Try to extract URL from outputs
            const outputToProcess = outputs;
            
            if (outputToProcess) {
              if (Array.isArray(outputToProcess) && outputToProcess.length > 0) {
                for (const output of outputToProcess) {
                  if (output.url && (output.url.endsWith('.webp') || output.url.endsWith('.mp4'))) {
                    finalUrl = output.url;
                    break;
                  } else if (output.data?.videos?.[0]?.url) {
                    finalUrl = output.data.videos[0].url;
                    break;
                  } else if (output.data?.images?.[0]?.url) {
                    const isWebp = output.data.images[0].url.endsWith('.webp');
                    if (isWebp) {
                      finalUrl = output.data.images[0].url;
                      break;
                    }
                  }
                }
              } else if (outputToProcess.url) {
                finalUrl = outputToProcess.url;
              } else if (outputToProcess.data?.videos?.[0]?.url) {
                finalUrl = outputToProcess.data.videos[0].url;
              } else if (outputToProcess.data?.images?.[0]?.url) {
                const isWebp = outputToProcess.data.images[0].url.endsWith('.webp');
                if (isWebp) {
                  finalUrl = outputToProcess.data.images[0].url;
                }
              }
            }
          }
          
          if (finalUrl) {
            // Upload to Supabase
            try {
              const response = await fetch(finalUrl);
              if (!response.ok) {
                throw new Error(`Failed to fetch video from ComfyDeploy: ${response.status}`);
              }
              
              const blob = await response.blob();
              const fileExtension = finalUrl.split('.').pop()?.toLowerCase() || 'webp';
              const fileName = `mimarai-video-${Date.now()}.${fileExtension}`;
              
              const videoFile = new File([blob], fileName, { 
                type: fileExtension === 'webp' ? 'image/webp' : 'video/mp4' 
              });
              
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('generated-videos')
                .upload(`${session.user.id}/${fileName}`, videoFile, {
                  cacheControl: '3600',
                  upsert: false
                });
                
              if (!uploadError && uploadData?.path) {
                const { data: urlData } = supabase.storage
                  .from('generated-videos')
                  .getPublicUrl(uploadData.path);
                  
                if (urlData?.publicUrl) {
                  finalUrl = urlData.publicUrl;
                  saveVideoMetadata(finalUrl);
                }
              }
            } catch (uploadError) {
              console.error('Error uploading to Supabase:', uploadError);
              // Continue with ComfyDeploy URL
            }
            
            resolve(finalUrl);
          } else {
            reject(new Error('No video URL found in outputs'));
          }
        }).catch(error => {
          reject(error);
        });
      });
    } catch (error) {
      console.error('Error in handleGenerateVideoWithSettings:', error);
      throw error;
    }
  };

  // Update video playback state based on video events
  useEffect(() => {
    const videoElement = videoRef.current;

    if (videoElement) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);

      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);

      // Sync initial state in case autoplay is enabled/works or video src changes
      setIsPlaying(!videoElement.paused && videoElement.readyState > 2); // Ensure video is ready

      // Cleanup listeners when the element or src changes
      return () => {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
      };
    } else {
      // Reset playing state if video element is not present
      setIsPlaying(false);
    }
    // Re-run this effect if the generatedVideo source changes
  }, [generatedVideo]);
  
  // Handle timeline item resize
  const handleTimelineItemResize = (outputId, newDuration) => {
    setVideoOutputs(videoOutputs.map(output => {
      if (output.id === outputId) {
        const clampedDuration = Math.max(0.5, Math.min(newDuration, totalDuration - output.startTime));
        return {
          ...output,
          endTime: output.startTime + clampedDuration
        };
      }
      return output;
    }));
  };
  
  // Initialize studio mode when switching to it
  useEffect(() => {
    if (mode === 'studio') {
      // Initialize keyframes if empty
      if (keyframes.length === 0) {
        // Create default start and end keyframes
        const startKeyframe = {
          id: Date.now(),
          time: 0,
          properties: {
            position: { x: 0, y: 0 },
            zoom: 1,
            rotation: 0
          }
        };
        
        const endKeyframe = {
          id: Date.now() + 1,
          time: parseInt(duration, 10),
          properties: {
            position: { x: 0, y: 0 },
            zoom: 1,
            rotation: 0
          }
        };
        
        setKeyframes([startKeyframe, endKeyframe]);
      }
      
      // Initialize asset library if empty and we have an uploaded image
      if (uploadedImage) {
        // Check if this image is already in the library
        const imageInLibrary = assetLibrary.some(asset => 
          asset.file && 
          asset.file.name === uploadedImage.name && 
          asset.file.size === uploadedImage.size
        );
        
        if (!imageInLibrary) {
          addAssetToLibrary(uploadedImage);
        }
      }
      
      // Set total duration based on current duration setting
      setTotalDuration(parseInt(duration, 10));
      
      // Create a sample video output if none exist
      if (videoOutputs.length === 0 && generatedVideo) {
        // If we already have a generated video, add it to the timeline
        // Use a unique ID with timestamp and random component
        const uniqueId = Date.now() + '-' + Math.floor(Math.random() * 10000);
        
        setVideoOutputs([{
          id: uniqueId,
          startTime: 0,
          endTime: 2,
          type: 'video',
          settings: {
            cameraMovement: cameraMovement,
            quality: quality,
            assetId: 'main'
          },
          status: 'completed',
          url: generatedVideo,
          createdAt: new Date().toISOString()
        }]);
        
        // Select the newly added output
        setSelectedOutput(uniqueId);
      }
      
      // Clean up any duplicate outputs (same startTime, endTime, and status)
      const uniqueOutputs = [];
      const outputKeys = new Set();
      
      for (const output of videoOutputs) {
        const key = `${output.startTime}-${output.endTime}-${output.status}`;
        if (!outputKeys.has(key)) {
          outputKeys.add(key);
          uniqueOutputs.push(output);
        }
      }
      
      if (uniqueOutputs.length !== videoOutputs.length) {
        console.log('Removing duplicate outputs', videoOutputs.length, uniqueOutputs.length);
        setVideoOutputs(uniqueOutputs);
      }
      
      // Set the main image as selected by default if nothing is selected
      if (!selectedAsset && uploadedImage) {
        selectAsset('main');
      }
    }
  }, [mode, duration, keyframes.length, uploadedImage, videoOutputs.length, generatedVideo, selectedAsset]);
  
  // Add a cleanup effect for asset library object URLs
  useEffect(() => {
    return () => {
      // Cleanup all object URLs in the asset library when component unmounts
      assetLibrary.forEach(asset => {
        if (asset.url && asset.url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(asset.url);
            console.log('Revoked Object URL for asset:', asset.name);
          } catch (error) {
            console.error('Error revoking object URL:', error);
          }
        }
      });
    };
  }, []);


  return (
    <Box sx={{
      maxWidth: '1100px', // Slightly wider max width
      width: '100%',
      margin: '0 auto',
      padding: {
        xs: '20px 15px',
        sm: '30px 20px', // Adjusted padding
        md: '40px 30px',
      },
      position: 'relative',
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(18, 18, 23, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: { xs: '16px', sm: '24px' }, // Consistent rounding
      boxShadow: theme => `0 8px 32px ${theme.palette.primary.main}20`,
      border: theme => `1px solid ${theme.palette.divider}`, // Use divider color for border
      overflow: 'hidden', // Keep overflow hidden
      backdropFilter: 'blur(10px)', // Slightly more blur
    }}>
      <Typography
        variant="h4"
        component="h1" // More semantic heading
        sx={{
          mb: 4,
          fontWeight: 800,
          textAlign: 'center',
          color: 'primary.main', // Use primary color for title
          textShadow: theme => theme.palette.mode === 'dark'
            ? '0 0 15px rgba(0, 206, 209, 0.3)' // Adjusted shadow
            : 'none',
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }, // Responsive font size
          position: 'relative',
          '&::after': { // Underline effect
            content: '""',
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80px',
            height: '4px',
            background: theme => `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
            borderRadius: '4px',
            boxShadow: theme => `0 2px 8px ${theme.palette.primary.main}40`
          }
        }}
      >
        İç Mekan Video Oluşturucu
      </Typography>

      {/* Global Error Alert */}
       {error && !isGenerating && (
           <Alert
             severity="error"
             sx={{ mb: 3, borderRadius: 2 }}
             action={
               <IconButton
                 aria-label="close"
                 color="inherit"
                 size="small"
                 onClick={() => {
                   setError(null);
                 }}
               >
                 <CloseIcon fontSize="inherit" />
               </IconButton>
             }
           >
             {error}
           </Alert>
       )}

      <Tabs
        value={mode}
        onChange={handleModeChange}
        centered
        indicatorColor="primary"
        textColor="primary"
        sx={{
          mb: 4,
          borderBottom: 1, // Add subtle border bottom
          borderColor: 'divider',
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          }
        }}
      >
        <StyledTab
          value="quick"
          label="Hızlı Dönüşüm"
          icon={<MovieIcon />}
          iconPosition="start"
        />
        <StyledTab
          value="studio"
          label="Stüdyo Modu"
          icon={<TuneIcon />}
          iconPosition="start"
        />
      </Tabs>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, // Give more space to preview
        gap: { xs: 3, md: 4 }, // Responsive gap
        mb: 4
      }}>
        {/* Left Column - Upload and Settings */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {mode === 'quick' ? (
            <UploadArea
              isDragging={isDragging}
              hasFile={!!uploadedImage}
              onClick={() => fileInputRef.current?.click()} // Optional chaining
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              aria-label="Görsel yükleme alanı" // Aria label for accessibility
            >
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/jpeg, image/png, image/webp" // Be specific about accepted types
                onChange={handleFileUpload}
              />

              {uploadedImage && previewUrl ? ( // Check for previewUrl as well
                <>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Yüklenen iç mekan önizlemesi" // Turkish alt text
                    sx={{
                      maxWidth: '80%', // Control preview size
                      maxHeight: 180, // Control preview size
                      objectFit: 'contain',
                      borderRadius: 2,
                      mb: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', textAlign: 'center', px: 1 }}>
                    {uploadedImage.name}
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ mt: 1, textAlign: 'center' }}>
                    Değiştirmek için tıklayın veya yeni bir görsel sürükleyin
                  </Typography>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Görsel Yükleyin
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Bir iç mekan görseli (.jpg, .png, .webp) sürükleyin veya tıklayarak seçin
                  </Typography>
                </>
              )}
            </UploadArea>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(18, 18, 23, 0.8)'
                  : 'rgba(255, 255, 255, 0.8)',
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Medya Kütüphanesi
                </Typography>
                <Box>
                  <Tooltip title="Görsel Ekle">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        fileInputRef.current?.click();
                      }}
                    >
                      <AddPhotoAlternateIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kütüphaneyi Temizle">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        if (assetLibrary.length > 0 && window.confirm('Medya kütüphanesindeki tüm görselleri silmek istediğinizden emin misiniz?')) {
                          // Revoke all object URLs to prevent memory leaks
                          assetLibrary.forEach(asset => {
                            if (asset.url && asset.url.startsWith('blob:')) {
                              URL.revokeObjectURL(asset.url);
                            }
                          });
                          setAssetLibrary([]);
                          // If main image is not in library, keep it
                          if (uploadedImage && !assetLibrary.some(a => a.url === previewUrl)) {
                            selectAsset('main');
                          } else {
                            setUploadedImage(null);
                            setPreviewUrl(null);
                            selectAsset(null);
                          }
                        }
                      }}
                      disabled={assetLibrary.length === 0}
                    >
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg, image/png, image/webp"
                  onChange={handleFileUpload}
                />
              </Box>
            
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: 1.5,
                  mb: 2,
                  minHeight: '150px',
                  border: theme => `1px dashed ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: isDragging ? 'action.hover' : 'transparent',
                  transition: 'background-color 0.2s ease'
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {/* We no longer need a separate "Main uploaded image" section */}
              
                {/* Additional assets */}
                {assetLibrary.map((asset) => (
                  <Paper
                    key={asset.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      aspectRatio: '1',
                      cursor: 'pointer',
                      border: selectedAsset === asset.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      boxShadow: selectedAsset === asset.id ? `0 0 0 2px ${theme.palette.primary.main}, 0 4px 8px rgba(0,0,0,0.2)` : '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}80, 0 4px 8px rgba(0,0,0,0.2)`,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                    onClick={() => {
                      selectAsset(asset.id);
                      // Set this as the main image for generation
                      if (asset.file) {
                        setUploadedImage(asset.file);
                        setPreviewUrl(asset.url);
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={asset.url}
                      alt={asset.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      loading="lazy"
                      onError={(e) => {
                        console.error('Error loading image:', e);
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iMTIiIHk9IjEyIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmaWxsPSIjYWFhYWFhIj5JbWFnZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '4px',
                      fontSize: '10px',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {asset.name.length > 15 ? asset.name.substring(0, 12) + '...' : asset.name}
                    </Box>
                    {selectedAsset === asset.id && (
                      <Box sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        <Box component="span" sx={{ color: 'white', fontSize: 14 }}>✓</Box>
                      </Box>
                    )}
                    <Box sx={{
                      position: 'absolute',
                      top: 5,
                      left: 5,
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: 1
                      }
                    }}>
                      <IconButton
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255,0,0,0.7)',
                          },
                          width: 20,
                          height: 20,
                          minWidth: 20,
                          minHeight: 20
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Remove this asset
                          if (window.confirm(`"${asset.name}" görselini silmek istediğinizden emin misiniz?`)) {
                            // Revoke object URL to prevent memory leaks
                            if (asset.url && asset.url.startsWith('blob:')) {
                              URL.revokeObjectURL(asset.url);
                            }
                          
                            // Remove from library
                            setAssetLibrary(prev => prev.filter(a => a.id !== asset.id));
                          
                            // If this was the selected asset, select main instead
                            if (selectedAsset === asset.id) {
                              selectAsset('main');
                            }
                          }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              
                {/* Upload placeholder - always show this for drag & drop */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: theme.palette.action.hover,
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    p: 1,
                    border: `1px dashed ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: theme.palette.action.selected,
                      borderColor: theme.palette.primary.main
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AddPhotoAlternateIcon sx={{ fontSize: 24, color: theme.palette.text.secondary, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" align="center">
                    Yeni Görsel Ekle
                  </Typography>
                </Paper>
              </Box>
            
              {/* Instructions if no image */}
              {!previewUrl && assetLibrary.length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  Video oluşturmak için en az bir görsel yüklemelisiniz
                </Typography>
              )}
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 }, // Responsive padding
              borderRadius: 3,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(18, 18, 23, 0.8)'
                : 'rgba(255, 255, 255, 0.8)',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Video Ayarları
              </Typography>

              <Tooltip title={showSettings ? "Ayarları Gizle" : "Ayarları Göster"}>
                <IconButton onClick={toggleSettings} color="primary" size="small" aria-expanded={showSettings}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Conditionally render settings based on showSettings state */}
            {showSettings && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="camera-movement-label">Kamera Hareketi</InputLabel>
                  <Select
                    labelId="camera-movement-label"
                    value={cameraMovement}
                    label="Kamera Hareketi"
                    onChange={(e) => setCameraMovement(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }} // Prevent overly long menus
                  >
                    <MenuItem value="pan_left">Sola Kaydırma</MenuItem>
                    <MenuItem value="pan_right">Sağa Kaydırma</MenuItem>
                    <MenuItem value="zoom_in">Yakınlaştırma</MenuItem>
                    <MenuItem value="zoom_out">Uzaklaştırma</MenuItem>
                    <MenuItem value="rotate_cw">Saat Yönünde Döndürme</MenuItem> {/* More specific */}
                    <MenuItem value="rotate_ccw">Saat Yönü Tersine Döndürme</MenuItem> {/* More specific */}
                    {/* <MenuItem value="dolly">Dolly Efekti</MenuItem> */} {/* Confirm if supported */}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="duration-label">Süre (saniye)</InputLabel>
                  <Select
                    labelId="duration-label"
                    value={duration}
                    label="Süre (saniye)"
                    onChange={(e) => setDuration(e.target.value)}
                  >
                    <MenuItem value="3">3 saniye</MenuItem>
                    <MenuItem value="4">4 saniye</MenuItem>
                    <MenuItem value="5">5 saniye</MenuItem>
                    <MenuItem value="6">6 saniye</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="quality-label">Kalite</InputLabel>
                  <Select
                    labelId="quality-label"
                    value={quality}
                    label="Kalite"
                    onChange={(e) => setQuality(e.target.value)}
                  >
                    <MenuItem value="draft">Taslak</MenuItem>
                    <MenuItem value="standard">Standart</MenuItem>
                    <MenuItem value="high">Yüksek</MenuItem>
                  </Select>
                </FormControl>

                {/* Studio mode specific settings (Placeholder) */}
                {mode === 'studio' && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Gelişmiş Ayarlar (Stüdyo - Aktif Değil)
                    </Typography>
                    {/* Add disabled placeholder controls here if needed */}
                  </>
                )}
              </Box>
            )}

            <ControlButton
              variant="contained"
              fullWidth
              disabled={!uploadedImage || isGenerating || !session} // Also disable if no session
              onClick={handleGenerateVideo}
              startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <MovieIcon />}
              sx={{ mt: showSettings ? 3 : 2 }} // Adjust margin based on settings visibility
              aria-live="polite" // Announce changes for screen readers
              id="generate-video-button" // Add ID for direct DOM access
            >
              {isGenerating ? 
                `Oluşturuluyor... ${Math.round(progress)}%` : 
                (generatedVideo ? 'Yeni Video Oluştur' : 'Video Oluştur')}
            </ControlButton>
            {!session && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', textAlign: 'center' }}>
                    Video oluşturmak ve galerinize kaydetmek için <Button size="small" onClick={() => {/* Trigger Login */}}>giriş yapın</Button>.
                </Typography>
            )}
          </Paper>
        </Box>

        {/* Right Column - Preview */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PreviewContainer>
            {/* Generated Video */}
            {generatedVideo && !isGenerating && !error && ( // Only show video if generated, not loading, and no error
              <>
                {generatedVideo.endsWith('.webp') ? (
                  // For WebP animations, use an img tag with special handling
                  <Box 
                    component="img"
                    src={generatedVideo}
                    alt="Oluşturulan animasyon"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000', // Explicit black background
                      borderRadius: 'inherit'
                    }}
                  />
                ) : (
                  // For MP4 videos, use video tag
                  <Box 
                    component="video"
                    ref={videoRef}
                    src={generatedVideo}
                    // autoPlay // Let user control playback
                    loop
                    muted // Muted recommended for autoplay if re-enabled
                    playsInline // Important for mobile
                    controls={false} // Hide default controls, use custom buttons
                    onLoadedData={() => { // Auto-play attempt on load (might be blocked)
                       videoRef.current?.play().catch(err => {
                           console.log("Autoplay prevented:", err);
                           setIsPlaying(false);
                       });
                    }}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000', // Explicit black background
                      borderRadius: 'inherit'
                    }}
                    aria-label="Oluşturulan video önizlemesi" // Accessibility
                  />
                )}
              </>
            )}

            {/* Placeholder when no video is successfully displayed and not generating */}
            {!generatedVideo && !isGenerating && !error && (
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <MovieIcon sx={{ fontSize: 60, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body1" >
                     Videolarınız burada görüntülenecek
                  </Typography>
              </Box>
            )}

            {/* Loading/Progress Indicator */}
            {isGenerating && (
              <Box 
                id="progress-overlay"
                sx={{
                  position: 'absolute',
                  inset: 0, // Replaces top, left, right, bottom
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  zIndex: 2,
                  borderRadius: 'inherit', // Inherit border radius
                  padding: 2, // Add padding
                  textAlign: 'center',
                }}
              >
                <CircularProgress
                  variant="determinate" // Always use determinate to show progress
                  value={Math.max(5, progress)} // Minimum 5% to show some progress
                  size={60}
                  thickness={4}
                  sx={{ mb: 2, color: 'primary.light' }}
                />
                <Typography variant="h6" color="white">
                  Video Oluşturuluyor...
                </Typography>
                <Typography 
                  variant="body2" 
                  color="rgba(255,255,255,0.8)" 
                  sx={{ mt: 1 }}
                  id="progress-text"
                >
                  %{Math.round(progress)} tamamlandı
                </Typography>
              </Box>
            )}
          </PreviewContainer>

          {/* Play/Pause, Download, Share Buttons */}
          {generatedVideo && !isGenerating && !error && (
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={togglePlayPause}
                startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                sx={{ borderRadius: 12, textTransform: 'none' }}
                aria-label={isPlaying ? "Videoyu duraklat" : "Videoyu oynat"}
              >
                {isPlaying ? 'Duraklat' : 'Oynat'}
              </Button>

              <Button
                variant="contained"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: 12, textTransform: 'none' }}
                aria-label="Videoyu indir"
              >
                İndir
              </Button>

              <Button
                variant="outlined"
                color="secondary" // Use secondary color for share
                onClick={handleShare}
                startIcon={<ShareIcon />}
                sx={{
                  borderRadius: 12,
                  textTransform: 'none',
                }}
                aria-label="Videoyu paylaş"
              >
                Paylaş
              </Button>
            </Box>
          )}

          {/* Studio Mode Interface */}
          {mode === 'studio' && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              width: '100%',
              height: '100%'
            }}>
              {/* Studio Header Bar */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(18, 18, 23, 0.8)'
                    : 'rgba(255, 255, 255, 0.8)',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Yeni Proje">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setKeyframes([]);
                        setSelectedKeyframe(null);
                        setCurrentTime(0);
                        alert('Yeni proje oluşturuldu');
                      }}
                    >
                      <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Aç">
                    <IconButton 
                      size="small"
                      onClick={() => alert('Proje açma işlevi')}
                    >
                      <FolderOpenIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kaydet">
                    <IconButton 
                      size="small"
                      onClick={() => alert('Proje kaydedildi')}
                    >
                      <SaveIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Geri Al">
                    <IconButton 
                      size="small"
                      onClick={() => alert('Geri alma işlevi')}
                    >
                      <UndoIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Yinele">
                    <IconButton 
                      size="small"
                      onClick={() => alert('Yineleme işlevi')}
                    >
                      <RedoIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Video Oluştur">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={handleGenerateVideo}
                      disabled={!uploadedImage || isGenerating || !session}
                    >
                      {isGenerating ? 
                        <CircularProgress size={20} color="inherit" /> : 
                        <PublishIcon sx={{ fontSize: 20 }} />
                      }
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Yardım">
                    <IconButton 
                      size="small"
                      onClick={() => alert('Stüdyo modu yardım bilgileri')}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
              
              {/* Two-Panel Layout (removed Önizleme panel) */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: '1fr 220px',
                gap: 2,
                height: '600px',
                minHeight: '500px',
                maxHeight: '800px',
                mb: 3
              }}>
                {/* Left Panel: Advanced Settings */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(18, 18, 23, 0.8)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gridRow: '1 / 2'
                  }}
                >
                  <Box sx={{ 
                    p: 1.5, 
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Gelişmiş Ayarlar
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    p: 1.5, 
                    overflowY: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    <Typography variant="body2" gutterBottom>
                      Seçili Görsel: {assetLibrary.find(a => a.id === selectedAsset)?.name || 
                        (uploadedImage && selectedAsset === 'main' ? uploadedImage.name : 'Seçilmedi')}
                    </Typography>
                    
                    <FormControl fullWidth size="small">
                      <InputLabel id="camera-path-label">Hareket Yolu</InputLabel>
                      <Select
                        labelId="camera-path-label"
                        value={cameraPath}
                        label="Hareket Yolu"
                        onChange={(e) => setCameraPath(e.target.value)}
                      >
                        <MenuItem value="linear">Doğrusal</MenuItem>
                        <MenuItem value="bezier">Bezier Eğrisi</MenuItem>
                        <MenuItem value="custom">Özel Yol</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Typography variant="body2" gutterBottom>
                      Hareket Hızı
                    </Typography>
                    <Slider
                      value={50}
                      onChange={(e, newValue) => {
                        console.log('Hareket hızı:', newValue);
                      }}
                      valueLabelDisplay="auto"
                    />
                    
                    <Typography variant="body2" gutterBottom>
                      Derinlik Efekti
                    </Typography>
                    <Slider
                      value={30}
                      onChange={(e, newValue) => {
                        console.log('Derinlik efekti:', newValue);
                      }}
                      valueLabelDisplay="auto"
                    />
                    
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        İpucu: Zaman çizelgesinde yeni video eklemek için + butonuna tıklayın
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                
                {/* Removed the Önizleme panel */}
                
                {/* Right Panel: Properties & Parameters */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(18, 18, 23, 0.8)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gridRow: '1 / 2'
                  }}
                >
                  <Box sx={{ 
                    p: 1.5, 
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Kamera Ayarları
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    p: 1.5, 
                    overflowY: 'auto',
                    flex: 1
                  }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="camera-movement-label">Kamera Hareketi</InputLabel>
                      <Select
                        labelId="camera-movement-label"
                        value={cameraMovement}
                        label="Kamera Hareketi"
                        onChange={(e) => setCameraMovement(e.target.value)}
                      >
                        <MenuItem value="pan_left">Sola Kaydırma</MenuItem>
                        <MenuItem value="pan_right">Sağa Kaydırma</MenuItem>
                        <MenuItem value="zoom_in">Yakınlaştırma</MenuItem>
                        <MenuItem value="zoom_out">Uzaklaştırma</MenuItem>
                        <MenuItem value="rotate_cw">Saat Yönünde Döndürme</MenuItem>
                        <MenuItem value="rotate_ccw">Saat Yönü Tersine Döndürme</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Typography variant="body2" gutterBottom>
                      Süre: {duration} saniye
                    </Typography>
                    <Slider
                      value={parseInt(duration, 10)}
                      min={2}
                      max={10}
                      step={1}
                      marks
                      onChange={(e, newValue) => setDuration(newValue.toString())}
                      valueLabelDisplay="auto"
                      sx={{ mb: 3 }}
                    />
                    
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="quality-label">Kalite</InputLabel>
                      <Select
                        labelId="quality-label"
                        value={quality}
                        label="Kalite"
                        onChange={(e) => setQuality(e.target.value)}
                      >
                        <MenuItem value="draft">Taslak</MenuItem>
                        <MenuItem value="standard">Standart</MenuItem>
                        <MenuItem value="high">Yüksek</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
                
                {/* Bottom Panel: Enhanced Timeline Interface */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(18, 18, 23, 0.8)'
                      : 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gridColumn: '1 / 4',
                    gridRow: '2 / 3'
                  }}
                >
                  <Box sx={{ 
                    p: 1.5, 
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 2 }}>
                        Zaman Çizelgesi
                      </Typography>
                      <Tooltip title="Video Oluştur">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={handleGenerateVideo}
                          disabled={!uploadedImage || isGenerating || !session}
                        >
                          {isGenerating ? 
                            <CircularProgress size={18} color="inherit" /> : 
                            <MovieIcon sx={{ fontSize: 18 }} />
                          }
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Yakınlaştır">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            const newZoom = Math.min(4, timelineZoom + 0.5);
                            setTimelineZoom(newZoom);
                            console.log('Timeline zoom set to:', newZoom);
                          }}
                        >
                          <ZoomInIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Uzaklaştır">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            const newZoom = Math.max(0.5, timelineZoom - 0.5);
                            setTimelineZoom(newZoom);
                            console.log('Timeline zoom set to:', newZoom);
                          }}
                        >
                          <ZoomOutIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Tümünü Göster">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setTimelineZoom(1);
                            console.log('Timeline zoom reset to 1');
                          }}
                        >
                          <FitScreenIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Timeline Ruler with Draggable Playhead and Zoom */}
                    <Box 
                      sx={{ 
                        height: '30px',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        cursor: 'pointer',
                        overflow: 'auto'
                      }}
                      onClick={(e) => {
                        // Calculate click position as percentage of timeline width
                        const rect = e.currentTarget.getBoundingClientRect();
                        const scrollLeft = e.currentTarget.scrollLeft;
                        const totalWidth = e.currentTarget.scrollWidth;
                        const clickPos = (e.clientX - rect.left + scrollLeft) / totalWidth;
                        // Set current time based on click position
                        setCurrentTime(clickPos * totalDuration);
                      }}
                    >
                      {/* Zoom container */}
                      <Box sx={{ 
                        position: 'relative',
                        width: `${timelineZoom * 100}%`,
                        height: '100%'
                      }}>
                        {/* Time markers */}
                        {Array.from({ length: parseInt(duration, 10) * 4 + 1 }).map((_, index) => {
                          const isMainMarker = index % 4 === 0;
                          return (
                            <Box key={index} sx={{ 
                              position: 'absolute',
                              left: `${(index / (parseInt(duration, 10) * 4)) * 100}%`,
                              height: isMainMarker ? '16px' : '8px',
                              borderLeft: `1px solid ${isMainMarker ? theme.palette.text.secondary : theme.palette.divider}`,
                              display: 'flex',
                              alignItems: 'center',
                              transform: 'translateX(-50%)'
                            }}>
                              {isMainMarker && (
                                <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 'bold' }}>
                                  {index/4}s
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                        
                        {/* Current time indicator (Playhead) */}
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            left: `${(currentTime / totalDuration) * 100}%`,
                            top: 0,
                            bottom: 0,
                            width: '3px',
                            backgroundColor: theme.palette.error.main,
                            zIndex: 10,
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-6px',
                              width: '15px',
                              height: '15px',
                              backgroundColor: theme.palette.error.main,
                              borderRadius: '50%',
                              transform: 'translateY(-50%)'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Tracks Container with Horizontal Scroll */}
                    <Box 
                      sx={{ 
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                      }}
                    >
                      {/* Vertical time indicator line with zoom */}
                      <Box sx={{ 
                        position: 'absolute',
                        left: `${(currentTime / totalDuration) * 100 * timelineZoom}%`,
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: theme.palette.error.main,
                        opacity: 0.7,
                        zIndex: 1,
                        pointerEvents: 'none'
                      }} />
                      
                      {/* Camera Track */}
                      <Box sx={{ 
                        height: '50px',
                        display: 'flex',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}>
                        {/* Track Label */}
                        <Box sx={{ 
                          width: '100px',
                          borderRight: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1.5,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                        }}>
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            Kamera
                          </Typography>
                        </Box>
                        
                        {/* Track Content with Zoom */}
                        <Box sx={{ 
                          flex: 1,
                          position: 'relative',
                          px: 1,
                          py: 1,
                          overflow: 'auto',
                          '& > *': {
                            transformOrigin: 'left center',
                            transform: `scaleX(${timelineZoom})`,
                            width: `${100 / timelineZoom}%`
                          }
                        }}>
                          {/* Keyframes */}
                          {keyframes.map((kf, index) => (
                            <Box 
                              key={kf.id}
                              sx={{ 
                                position: 'absolute',
                                left: `${(kf.time / totalDuration) * 100}%`,
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '14px',
                                height: '14px',
                                backgroundColor: selectedKeyframe === kf.id 
                                  ? theme.palette.secondary.main 
                                  : theme.palette.primary.main,
                                borderRadius: '3px',
                                cursor: 'pointer',
                                zIndex: 2,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                border: selectedKeyframe === kf.id 
                                  ? `2px solid ${theme.palette.secondary.light}` 
                                  : `1px solid ${theme.palette.primary.light}`,
                                '&:hover': {
                                  transform: 'translate(-50%, -50%) scale(1.2)',
                                }
                              }}
                              onClick={() => {
                                setSelectedKeyframe(kf.id);
                                setCurrentTime(kf.time);
                              }}
                            />
                          ))}
                          
                          {/* Track line with gradient - larger for better visibility */}
                          <Box sx={{ 
                            position: 'absolute',
                            left: '0%',
                            right: '0%',
                            top: '50%',
                            height: '6px',
                            background: `linear-gradient(90deg, 
                              ${theme.palette.primary.main}, 
                              ${theme.palette.secondary.main})`,
                            opacity: 0.8,
                            transform: 'translateY(-50%)',
                            borderRadius: '3px'
                          }} />
                        </Box>
                      </Box>
                      
                      {/* Video Outputs Track */}
                      <Box sx={{ 
                        height: '80px',
                        display: 'flex',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}>
                        {/* Track Label */}
                        <Box sx={{ 
                          width: '100px',
                          borderRight: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1.5,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                        }}>
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            Videolar
                          </Typography>
                        </Box>
                        
                        {/* Track Content with class for scrolling */}
                        <Box 
                          className="timeline-track"
                          sx={{ 
                            flex: 1,
                            position: 'relative',
                            px: 1,
                            py: 1,
                            overflowX: 'auto'
                          }}
                        >
                          {/* Video Output Clips - Stable and Bug-Free */}
                          {/* Sort outputs by startTime to maintain consistent order */}
                          {[...videoOutputs]
                            .sort((a, b) => a.startTime - b.startTime)
                            .map((output, index) => {
                              // Calculate vertical position to prevent overlap
                              // Use a more deterministic approach based on id to prevent jumping
                              const verticalPosition = output.id % 2; 
                              
                              return (
                                <Box 
                                  key={`video-output-${output.id}`}
                                  sx={{ 
                                    position: 'absolute',
                                    left: `${(output.startTime / totalDuration) * 100}%`,
                                    width: `${((output.endTime - output.startTime) / totalDuration) * 100}%`,
                                    top: `${5 + (verticalPosition * 45)}%`, // More space between rows
                                    height: '40%', // Larger height for better visibility
                                    backgroundColor: output.status === 'completed' 
                                      ? theme.palette.success.main + '40'
                                      : output.status === 'error'
                                        ? theme.palette.error.main + '40'
                                        : output.status === 'generating'
                                          ? theme.palette.warning.main + '40'
                                          : theme.palette.primary.main + '30',
                                    borderRadius: '4px',
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    border: `2px solid ${
                                      selectedOutput === output.id
                                        ? theme.palette.secondary.main
                                        : output.status === 'completed' 
                                          ? theme.palette.success.main
                                          : output.status === 'error'
                                            ? theme.palette.error.main
                                            : output.status === 'generating'
                                              ? theme.palette.warning.main
                                              : theme.palette.primary.main
                                    }`,
                                    boxShadow: selectedOutput === output.id
                                      ? `0 0 0 2px ${theme.palette.secondary.main}, 0 4px 8px rgba(0,0,0,0.3)`
                                      : '0 2px 4px rgba(0,0,0,0.2)',
                                    '&:hover': {
                                      opacity: 0.9,
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                      zIndex: 10
                                    },
                                    '&:active': {
                                      cursor: 'grabbing',
                                      opacity: 0.8
                                    },
                                    zIndex: selectedOutput === output.id ? 5 : 1,
                                    transition: 'left 0.3s ease, width 0.3s ease, box-shadow 0.2s ease, transform 0.2s ease',
                                  }}
                                  onClick={() => {
                                    // Select this output
                                    setSelectedOutput(output.id);
                                    // If completed, show the video
                                    if (output.status === 'completed' && output.url) {
                                      // Force a refresh of the video by setting to null first
                                      setGeneratedVideo(null);
                                      // Use setTimeout to ensure state update happens in separate render cycle
                                      setTimeout(() => {
                                        setGeneratedVideo(output.url);
                                      }, 50);
                                    }
                                  }}
                                >
                                  {/* Thumbnail or status indicator */}
                                  {output.status === 'completed' && output.url ? (
                                    <Box 
                                      component="img"
                                      src={output.url}
                                      alt="Video thumbnail"
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      loading="lazy"
                                      onError={(e) => {
                                        console.error('Error loading thumbnail:', e);
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iMTIiIHk9IjEyIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmaWxsPSIjYWFhYWFhIj5JbWFnZTwvdGV4dD48L3N2Zz4=';
                                      }}
                                    />
                                  ) : output.status === 'generating' ? (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: '100%',
                                      width: '100%',
                                      p: 1
                                    }}>
                                      <CircularProgress size={24} color="inherit" />
                                      <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>
                                        Oluşturuluyor...
                                      </Typography>
                                    </Box>
                                  ) : output.status === 'error' ? (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: '100%',
                                      width: '100%',
                                      p: 1
                                    }}>
                                      <CloseIcon sx={{ color: theme.palette.error.main, fontSize: 24 }} />
                                      <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>
                                        Hata Oluştu
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: '100%',
                                      width: '100%',
                                      p: 1
                                    }}>
                                      <Box component="span" sx={{ 
                                        width: 24, 
                                        height: 24, 
                                        borderRadius: '50%',
                                        border: `2px solid ${theme.palette.text.secondary}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <PlayArrowIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                      </Box>
                                      <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>
                                        Bekliyor
                                      </Typography>
                                    </Box>
                                  )}
                                  
                                  {/* Clip actions */}
                                  <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    display: 'flex',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    borderRadius: '0 0 0 4px'
                                  }}>
                                    {output.status === 'pending' && (
                                      <Tooltip title="Oluştur">
                                        <IconButton 
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Generate this specific output
                                            handleGenerateVideoOutput(output.id);
                                          }}
                                        >
                                          <PlayArrowIcon sx={{ fontSize: 14, color: 'white' }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {output.status !== 'generating' && (
                                      <Tooltip title="Sil">
                                        <IconButton 
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Remove this output
                                            setVideoOutputs(prevOutputs => 
                                              prevOutputs.filter(v => v.id !== output.id)
                                            );
                                            if (selectedOutput === output.id) {
                                              setSelectedOutput(null);
                                            }
                                          }}
                                        >
                                          <CloseIcon sx={{ fontSize: 14, color: 'white' }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  
                                  {/* Duration label */}
                                  <Box sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    fontSize: '10px',
                                    padding: '2px 4px',
                                    borderRadius: '0 4px 0 0'
                                  }}>
                                    {Math.round((output.endTime - output.startTime) * 10) / 10}s
                                  </Box>
                                </Box>
                              );
                            })}
                          
                          {/* Clickable timeline background for placing videos */}
                          <Box 
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              if (!selectedOutput) return;
                              
                              const output = videoOutputs.find(o => o.id === selectedOutput);
                              if (!output) return;
                              
                              // Calculate click position as time
                              const trackRect = e.currentTarget.getBoundingClientRect();
                              const clickPos = (e.clientX - trackRect.left) / trackRect.width;
                              const newTime = clickPos * totalDuration;
                              const duration = output.endTime - output.startTime;
                              
                              // Update the output position
                              setVideoOutputs(prevOutputs => 
                                prevOutputs.map(o => {
                                  if (o.id === selectedOutput) {
                                    return {
                                      ...o,
                                      startTime: Math.max(0, Math.min(newTime, totalDuration - duration)),
                                      endTime: Math.min(totalDuration, Math.max(duration, newTime + duration))
                                    };
                                  }
                                  return o;
                                })
                              );
                            }}
                          />
                          
                          {/* Empty state message */}
                          {videoOutputs.length === 0 && (
                            <Box sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              opacity: 0.7
                            }}>
                              <Typography variant="caption" color="text.secondary">
                                Video oluşturduğunuzda otomatik olarak zaman çizelgesine eklenecektir
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Effects Track */}
                      <Box sx={{ 
                        height: '50px',
                        display: 'flex',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}>
                        {/* Track Label */}
                        <Box sx={{ 
                          width: '100px',
                          borderRight: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1.5,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                        }}>
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            Efektler
                          </Typography>
                        </Box>
                        
                        {/* Track Content */}
                        <Box sx={{ 
                          flex: 1,
                          position: 'relative',
                          px: 1
                        }}>
                          {/* Empty track with placeholder */}
                          <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.5
                          }}>
                            <Typography variant="caption" color="text.secondary">
                              Yakında...
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
              
              {/* We don't need a separate generate button for studio mode since we have the export button in the header */}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VideoGenerator;
